import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { usePermissions } from '../../utils/permissions';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  CircularProgress,
  Alert,
  Tooltip,
  Card,
  CardContent,
  CardActions,
  Divider,
  Stack,
  Chip,
  InputAdornment,
  useTheme,
  alpha,
  Link as MuiLink,
  Snackbar,
  Fade,
  Checkbox,
  Switch,
  FormControlLabel,
  LinearProgress
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  AttachMoney as MoneyIcon,
  Assignment as AssignmentIcon,
  Business as BusinessIcon,
  Storefront as StorefrontIcon,
  Print as PrintIcon,
  MoreVert as MoreVertIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  Home as HomeIcon,
  MonetizationOn as MonetizationOnIcon,
  Close as CloseIcon,
  WarningAmber as WarningAmberIcon,
  Person as PersonIcon,
  LocalOffer as LocalOfferIcon,
  DragIndicator as DragIndicatorIcon,
  Gavel as GavelIcon,
  FolderOpen as FolderOpenIcon,
  SaveAs as SaveAsIcon
} from '@mui/icons-material';

const getBrandIdForItem = (item) => item?.products?.brands?.id || 'no-brand';

const getVariantSizeFromItem = (item) => {
  const variantName = item?.product_variants?.name || item?.variant_name || '';
  const match = variantName.match(/(\d+(?:\.\d+)?)/);
  if (match) return parseFloat(match[1]);

  const volumeWeight = item?.product_variants?.volume_weight;
  if (volumeWeight) return parseFloat(volumeWeight);

  return 0;
};

const sortItemsByProductAndSize = (items) => {
  if (!items || items.length === 0) return items;

  return [...items].sort((a, b) => {
    const productNameA = a?.products?.name || '';
    const productNameB = b?.products?.name || '';
    if (productNameA !== productNameB) {
      return productNameA.localeCompare(productNameB);
    }

    const sizeA = getVariantSizeFromItem(a);
    const sizeB = getVariantSizeFromItem(b);
    return sizeB - sizeA;
  });
};

const moveItemInArray = (items, fromItemId, toItemId) => {
  const fromIndex = items.indexOf(fromItemId);
  const toIndex = items.indexOf(toItemId);
  if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return items;

  const next = [...items];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
};

export default function ProductPriceListPage() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { hasPermission } = usePermissions();

  // Check for URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const shouldAutoCreate = urlParams.get('create') === 'true';

  // State variables
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [priceLists, setPriceLists] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(shouldAutoCreate);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedPriceList, setSelectedPriceList] = useState(null);
  const [currentPage, setCurrentPage] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [priceListToDelete, setPriceListToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [showExpirationWarning, setShowExpirationWarning] = useState(false);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Customer search state
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  
  // Customer selection dialog state
  const [customerSelectionDialogOpen, setCustomerSelectionDialogOpen] = useState(false);

  // New price list form
  const [newPriceList, setNewPriceList] = useState({
    name: '',
    description: '',
    type: 'retail', // default type
    effective_date: (() => {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    })(),
    expiration_date: '',
    customer_id: null,
    is_active: true
  });

  // Price list types
  const priceListTypes = [
    { value: 'retail', label: 'Retail', color: 'primary' },
    { value: 'wholesale', label: 'Wholesale', color: 'secondary' },
    { value: 'distributor', label: 'Distributor', color: 'success' },
    { value: 'special', label: 'Special Promotion', color: 'warning' },
    { value: 'customer', label: 'Customer Specific', color: 'info' }
  ];

  // Helper function to sort variants by size in descending order
  const sortVariantsBySize = (variants) => {
    if (!variants || variants.length === 0) return variants;
    
    return [...variants].sort((a, b) => {
      // Extract numeric value from variant name or volume_weight
      const getNumericValue = (variant) => {
        // Try to get from name first (e.g., "48oz", "24oz", "16oz")
        if (variant.name) {
          const match = variant.name.match(/(\d+(?:\.\d+)?)/);
          if (match) return parseFloat(match[1]);
        }
        // Fallback to volume_weight
        if (variant.volume_weight) {
          return parseFloat(variant.volume_weight);
        }
        return 0;
      };
      
      const valueA = getNumericValue(a);
      const valueB = getNumericValue(b);
      
      // Sort in descending order (largest first)
      return valueB - valueA;
    });
  };

  // Fetch data function - defined before useEffect to avoid hoisting issues
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load company info
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();
      
      if (companyError) throw companyError;
      setCompanyName(companyData?.name || 'Company');

      // Fetch all price lists
      const { data: priceListsData, error: priceListsError } = await supabase
        .from('price_lists')
        .select(`
          *,
          customer:customer_id(id, name, company_id)
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });
      
      if (priceListsError) throw priceListsError;
      setPriceLists(priceListsData || []);
      
      // Get products with ALL fields including price information
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select(`
          *,
          brand:brand_id(id, name),
          variants:product_variants(
            id, 
            name, 
            sku, 
            barcode, 
            price, 
            cost_price,
            volume_weight,
            volume_unit,
            attributes
          )
        `)
        .eq('company_id', companyId)
        .order('name');
      
      if (productsError) throw productsError;
      
      // Debug product data and sort variants
      productsData.forEach(p => {
        
        // Debug variant prices
        if (p.variants && p.variants.length > 0) {
          p.variants.forEach(v => {
            
          });
          // Sort variants by size in descending order
          p.variants = sortVariantsBySize(p.variants);
        }
      });
      
      setProducts(productsData || []);
      
      // Fetch all customers for this company
      const { data: customersData, error: customersError } = await supabase
        .from('customers')
        .select('id, name')
        .eq('company_id', companyId)
        .order('name');
      
      if (customersError) throw customersError;
      setCustomers(customersData || []);
      
    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Check for expiring price lists
  const getExpirationStatus = (priceList) => {
    if (!priceList.expiration_date) return null;
    
    const today = new Date();
    const expirationDate = new Date(priceList.expiration_date);
    const daysUntilExpiration = Math.ceil((expirationDate - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiration < 0) {
      return { type: 'expired', days: Math.abs(daysUntilExpiration), message: `Expired ${Math.abs(daysUntilExpiration)} day(s) ago` };
    } else if (daysUntilExpiration <= 7) {
      return { type: 'expiring', days: daysUntilExpiration, message: `Expires in ${daysUntilExpiration} day(s)` };
    } else if (daysUntilExpiration <= 30) {
      return { type: 'warning', days: daysUntilExpiration, message: `Expires in ${daysUntilExpiration} day(s)` };
    }
    
    return null;
  };

  // Get expiring price lists for warnings
  const getExpiringPriceLists = () => {
    return priceLists.filter(priceList => {
      const status = getExpirationStatus(priceList);
      return status && (status.type === 'expired' || status.type === 'expiring');
    });
  };

  // Load data on component mount
  useEffect(() => {
    // Update document title
    document.title = `${companyName || 'Company'} - Price Lists Management`;
    fetchData();
  }, [companyId, companyName]);

  // Clean up URL parameter after auto-opening create dialog
  useEffect(() => {
    if (shouldAutoCreate && showCreateForm) {
      // Remove the create parameter from URL without causing a page reload
      const newUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [shouldAutoCreate, showCreateForm]);

  // Check for expiring price lists when price lists change
  useEffect(() => {
    const expiringLists = getExpiringPriceLists();
    if (expiringLists.length > 0) {
      setShowExpirationWarning(true);
    }
  }, [priceLists]);

  // Permission check
  if (!hasPermission('sales.price_lists.view')) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{ 
            p: 8, 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <LocalOfferIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Price Lists Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access the Price Lists. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  // Helper functions for price calculation
  const getVariantPrice = (variant) => {
    if (!variant) return 0;
    
    // For consistency, check in a specific order with clear fallbacks
    if (variant.price !== undefined && variant.price !== null) {
      return variant.price;
    }
    
    // Legacy field names as fallbacks
    if (variant.retail_price !== undefined && variant.retail_price !== null) {
      return variant.retail_price;
    }
    
    if (variant.default_price !== undefined && variant.default_price !== null) {
      return variant.default_price;
    }
    
    // Try to get from attributes if present
    if (variant.attributes && variant.attributes.price) {
      return variant.attributes.price;
    }
    
    return 0;
  };
  
  const getProductPrice = (product) => {
    if (!product) return 0;
    
    // For consistency, check in a specific order with clear fallbacks
    if (product.default_price !== undefined && product.default_price !== null) {
      return product.default_price;
    }
    
    // Legacy field names as fallbacks
    if (product.retail_price !== undefined && product.retail_price !== null) {
      return product.retail_price;
    }
    
    return 0;
  };



  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewPriceList(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Create a new price list
  const handleCreatePriceList = async () => {
    // Check permissions before proceeding
    if (!hasPermission('sales.price_lists.edit', 'write')) {
      setError('You do not have permission to create price lists.');
      return;
    }
    
    try {
      if (!newPriceList.name) {
        setError('Price list name is required');
        return;
      }
      
      // Create the price list
      const priceListData = {
        company_id: companyId,
        name: newPriceList.name,
        description: newPriceList.description,
        type: newPriceList.type,
        effective_date: newPriceList.effective_date,
        expiration_date: newPriceList.expiration_date || null,
        customer_id: newPriceList.customer_id || null,
        is_active: true,
        created_at: new Date().toISOString()
      };
      
      const { data, error } = await supabase
        .from('price_lists')
        .insert([priceListData])
        .select();
      
      if (error) throw error;
      
      // Add this new price list to our state
      const newList = data[0];
      
      // Add customer info if applicable
      if (newList.customer_id) {
        const customer = customers.find(c => c.id === newList.customer_id);
        newList.customer = customer;
      }
      
      setPriceLists([newList, ...priceLists]);
      setSuccess('Price list created successfully');
      setShowCreateForm(false);
      
      // Redirect to edit this price list
      handleEditPriceList(newList);
      
    } catch (err) {
      setError(err.message || 'Failed to create price list');
    }
  };

  // Delete a price list
  const handleDeleteClick = (priceListId, priceListName) => {
    const priceList = priceLists.find(p => p.id === priceListId);
    setPriceListToDelete({ id: priceListId, name: priceListName || priceList?.name || 'this price list' });
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!priceListToDelete) return;

    // Check permissions before proceeding
    if (!hasPermission('sales.price_lists.delete', 'delete')) {
      setSnackbar({
        open: true,
        message: 'You do not have permission to delete price lists.',
        severity: 'error'
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('price_lists')
        .delete()
        .eq('id', priceListToDelete.id);
      
      if (error) throw error;
      
      // Remove from state
      setPriceLists(priceLists.filter(list => list.id !== priceListToDelete.id));
      
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Error deleting price list: ${err.message}`,
        severity: 'error'
      });
    }

    setDeleteConfirmOpen(false);
    setPriceListToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setPriceListToDelete(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Edit a price list
  const handleEditPriceList = (priceList) => {
    setSelectedPriceList(priceList);
    setCurrentPage('edit');
  };

  // Print a price list
  const handlePrintPriceList = (priceList, e) => {
    if (e) e.stopPropagation();
    // Set the selected price list and edit it
    setSelectedPriceList({...priceList, autoPrint: true});
    setCurrentPage('edit');
    // The PriceListEditor will handle the printing
  };

  // Helper function to format company address 
  const formatCompanyAddress = (company) => {
    if (!company) return '';
    
    let address = '';
    
    if (company.address) address += company.address;
    
    if (company.city || company.state || company.zip_code) {
      address += address ? '\n' : '';
      if (company.city) address += company.city;
      if (company.state) {
        if (company.city) address += ', ';
        address += company.state;
      }
      if (company.zip_code) {
        if (company.city || company.state) address += ' ';
        address += company.zip_code;
      }
    }
    
    return address;
  };


  // View a price list directly with print preview
  const handleViewPriceList = async (priceList, e) => {
    if (e) e.stopPropagation();
    
    try {
      // Fetch price list items directly
      const { data: items, error } = await supabase
        .from('price_list_items')
        .select(`
          *
        `)
        .eq('price_list_id', priceList.id);
      
      if (error) throw error;
      
      // Get products and variants separately
      const productIds = [...new Set(items.map(item => item.product_id))];
      const variantIds = items.filter(item => item.variant_id).map(item => item.variant_id);
      
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select('*, brands(*)')
        .in('id', productIds);
        
      if (productsError) throw productsError;
      
      let variantsData = [];
      if (variantIds.length > 0) {
        const { data: variants, error: variantsError } = await supabase
          .from('product_variants')
          .select('*')
          .in('id', variantIds);
          
        if (variantsError) throw variantsError;
        variantsData = variants || [];
      }
      
      // Merge the data
      const enhancedItems = items.map(item => {
        const product = productsData.find(p => p.id === item.product_id);
        const variant = variantsData.find(v => v.id === item.variant_id);
        
        return {
          ...item,
          products: product,
          product_variants: variant
        };
      });

      // Get full company information
      let currentCompany = { name: companyName || 'Your Company' };
      try {
        const { data: companyData, error: companyError } = await supabase
          .from('companies')
          .select('name, address, city, state, zip_code, phone, email, website')
          .eq('id', companyId)
          .single();
          
        if (!companyError && companyData) {
          currentCompany = companyData;
        }
      } catch (err) {
        // Use default if company fetch fails
      }

      // Build itemsByBrand
       const currentItemsByBrand = {};
       enhancedItems.forEach(item => {
        if (item.products?.brands?.id) {
          const brandId = item.products.brands.id;
          if (!currentItemsByBrand[brandId]) {
            currentItemsByBrand[brandId] = {
              brand: item.products.brands,
              items: []
            };
          }
          currentItemsByBrand[brandId].items.push(item);
        } else {
          if (!currentItemsByBrand['no-brand']) {
            currentItemsByBrand['no-brand'] = {
              brand: { name: 'No Brand' },
              items: []
            };
          }
          currentItemsByBrand['no-brand'].items.push(item);
        }
      });

      // Keep same products together and order variants by size (largest first)
      Object.keys(currentItemsByBrand).forEach(brandId => {
        const defaultOrder = sortItemsByProductAndSize(currentItemsByBrand[brandId].items);
        const itemsWithOrder = currentItemsByBrand[brandId].items
          .filter(item => item.sort_order !== null && item.sort_order !== undefined)
          .sort((a, b) => a.sort_order - b.sort_order);

        if (itemsWithOrder.length > 0) {
          currentItemsByBrand[brandId].items = itemsWithOrder;
          return;
        }

        let storedOrderByBrand = null;
        try {
          storedOrderByBrand = JSON.parse(
            localStorage.getItem(`priceListOrder:${priceList.id}`) || 'null'
          );
        } catch (err) {
          storedOrderByBrand = null;
        }

        const storedOrder = Array.isArray(storedOrderByBrand?.[brandId])
          ? storedOrderByBrand[brandId]
          : [];

        if (storedOrder.length === 0) {
          currentItemsByBrand[brandId].items = defaultOrder;
          return;
        }

        const itemMap = new Map(defaultOrder.map(item => [item.id, item]));
        const orderedItems = [];

        storedOrder.forEach(id => {
          const item = itemMap.get(id);
          if (item) {
            orderedItems.push(item);
            itemMap.delete(id);
          }
        });

        if (itemMap.size > 0) {
          orderedItems.push(...Array.from(itemMap.values()));
        }

        currentItemsByBrand[brandId].items = orderedItems;
      });
      
      const printCSS = `
        @page { 
          size: auto; 
          margin: 20mm 15mm 20mm 15mm; 
        }
        @media print {
          body { 
            padding: 0 !important; 
            -webkit-print-color-adjust: exact;
            font-size: 10pt;
          }
          .no-print { 
            display: none !important; 
          }
          @page {
            margin: 20mm 15mm 20mm 15mm;
            @top-left { content: ""; }
            @top-center { content: ""; }
            @top-right { content: ""; }
            @bottom-left { content: ""; }
            @bottom-center { content: ""; }
            @bottom-right { content: ""; }
          }
        }
        body {
          font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;
          line-height: 1.3;
          color: #000;
          padding: 16px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin: 10px 0;
          font-size: 11px;
        }
        th, td {
          border: 1px solid #ddd;
          padding: 6px;
          text-align: left;
        }
        th {
          background-color: #f5f5f5;
          font-weight: 600;
          font-size: 11px;
        }
        .brand-header {
          background-color: #f0f0f0;
          font-weight: 600;
          font-size: 12px;
        }
        .price {
          text-align: right;
        }
      `;

      // Current date/time for the footer only
      const today = new Date();
      const dateString = today.toLocaleDateString();
      const timeString = today.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      const printHTML = `
          <div style="display: flex; justify-content: space-between; gap: 60px; margin-bottom: 20px;">
            <div style="flex: 1; max-width: 45%;">
              <h1 style="margin: 0 0 6px 0; font-size: 1.75rem; font-weight: 400; word-wrap: break-word; line-height: 1.2;">${priceList.name}</h1>
              <p style="margin: 2px 0; color: rgba(0, 0, 0, 0.6); font-size: 0.8rem;">
                Type: ${priceList.type.charAt(0).toUpperCase() + priceList.type.slice(1)}
              </p>
              ${priceList.customer ? 
                `<p style="margin: 2px 0; color: rgba(0, 0, 0, 0.6); font-size: 0.8rem;">
                  Customer: ${priceList.customer.name}
                </p>` : ''}
              <p style="margin: 2px 0; color: rgba(0, 0, 0, 0.6); font-size: 0.8rem;">
                Effective Date: ${formatDate(priceList.effective_date)}
              </p>
              ${priceList.expiration_date ? 
                `<p style="margin: 2px 0; color: rgba(0, 0, 0, 0.6); font-size: 0.8rem;">
                  Expiration Date: ${formatDate(priceList.expiration_date)}
                </p>` : ''}
            </div>
            <div style="text-align: right; flex-shrink: 0;">
              <h2 style="margin: 0 0 6px 0; font-size: 1.1rem; font-weight: 500;">
                ${currentCompany.name}
              </h2>
              ${formatCompanyAddress(currentCompany) ? `<p style="margin: 1px 0; font-size: 0.8rem;">
                ${formatCompanyAddress(currentCompany)}
              </p>` : ''}
              ${currentCompany.phone ? `<p style="margin: 1px 0; font-size: 0.8rem;">
                Phone: ${currentCompany.phone}
              </p>` : ''}
              ${currentCompany.email ? `<p style="margin: 1px 0; font-size: 0.8rem;">
                Email: ${currentCompany.email}
              </p>` : ''}
              ${currentCompany.website ? `<p style="margin: 1px 0; font-size: 0.8rem;">
                Web: ${currentCompany.website}
              </p>` : ''}
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Product</th>
                <th>Size</th>
                <th>Barcode / UPC</th>
                <th>Brand</th>
                ${priceList.show_case_price ? '<th class="price">Case Price</th>' : ''}
                ${priceList.show_pallet_price ? '<th class="price">Pallet Price</th>' : ''}
                <th class="price">MSRP</th>
              </tr>
            </thead>
            <tbody>
              ${Object.keys(currentItemsByBrand).map(brandId => {
                const brandGroup = currentItemsByBrand[brandId];
                return `
                  <tr class="brand-header">
                    <td colspan="${5 + (priceList.show_case_price ? 1 : 0) + (priceList.show_pallet_price ? 1 : 0)}">${brandGroup.brand.name}</td>
                  </tr>
                  ${brandGroup.items.map(item => {
                    // Calculate prices
                    let basePrice = 0;
                    if (item.variant_id && item.product_variants) {
                      basePrice = getVariantPrice(item.product_variants);
                    } else if (item.is_jsonb_variant) {
                      try {
                        if (item.notes) {
                          const variantData = JSON.parse(item.notes);
                          basePrice = variantData.price || 0;
                        }
                      } catch (e) {
                        
                      }
                    } else {
                      basePrice = getProductPrice(item.products);
                    }
                    
                    // Get variant display name
                    let variantName = '-';
                    if (item.product_variants?.name) {
                      variantName = item.product_variants.name;
                    } else if (item.is_jsonb_variant && item.variant_name) {
                      variantName = item.variant_name;
                    }
                    
                    return `
                      <tr>
                        <td>${item.products?.name || ''}</td>
                        <td>${variantName}</td>
                        <td>${item.product_variants?.barcode || item.products?.barcode || '-'}</td>
                        <td>${item.products?.brands?.name || 'No Brand'}</td>
                        ${priceList.show_case_price ? `<td class="price">${formatCurrency(item.case_price || 0)}</td>` : ''}
                        ${priceList.show_pallet_price ? `<td class="price">${formatCurrency(item.pallet_price || 0)}</td>` : ''}
                        <td class="price">${formatCurrency(basePrice)}</td>
                      </tr>
                    `;
                  }).join('')}
                `;
              }).join('')}
            </tbody>
          </table>
        ${priceList.terms_and_conditions ? `
          <div class="terms-box" style="margin: 20px 0; padding: 12px; border: 1px solid #000;">
            <h3 style="margin: 0 0 10px 0; font-weight: 600; font-size: 0.9rem;">Terms & Conditions</h3>
            <div style="white-space: pre-line; font-size: 0.75rem; line-height: 1.4;">
              ${priceList.terms_and_conditions}
            </div>
          </div>
        ` : ''}
        </body>
        </html>
      `;
      
      const iframeId = 'price-list-print-frame';
      let iframe = document.getElementById(iframeId);
      
      if (iframe) {
        document.body.removeChild(iframe);
      }
      
      iframe = document.createElement('iframe');
      iframe.id = iframeId;
      iframe.style.position = 'absolute';
      iframe.style.top = '-9999px';
      iframe.style.left = '-9999px';
      iframe.style.width = '0';
      iframe.style.height = '0';
      document.body.appendChild(iframe);
      
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      
      iframeDoc.open();
      iframeDoc.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title></title>
          <style>${printCSS}</style>
        </head>
        <body>
          ${printHTML}
        </body>
        </html>
      `);
      iframeDoc.close();
      
      iframe.onload = () => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
          
          setTimeout(() => {
            if (document.body.contains(iframe)) {
              document.body.removeChild(iframe);
            }
          }, 1000);
        } catch (error) {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
        }
      };
      
    } catch (err) {
      setError(err.message || 'Failed to view price list');
    }
  };

  // Open edit details dialog
  const handleEditDetails = (priceList, e) => {
    if (e) e.stopPropagation();
    setSelectedPriceList(priceList);
    setNewPriceList({
      name: priceList.name,
      description: priceList.description || '',
      type: priceList.type,
      effective_date: priceList.effective_date,
      expiration_date: priceList.expiration_date || '',
      customer_id: priceList.customer_id || null,
      is_active: priceList.is_active
    });
    setShowEditForm(true);
  };

  // Save edited price list
  const handleSavePriceList = async () => {
    // Check permissions before proceeding
    if (!hasPermission('sales.price_lists.edit', 'write')) {
      setError('You do not have permission to edit price lists.');
      return;
    }
    
    try {
      if (!newPriceList.name) {
        setError('Price list name is required');
        return;
      }
      
      // Update the price list
      const priceListData = {
        name: newPriceList.name,
        description: newPriceList.description,
        type: newPriceList.type,
        effective_date: newPriceList.effective_date,
        expiration_date: newPriceList.expiration_date || null,
        customer_id: newPriceList.customer_id || null,
        is_active: newPriceList.is_active,
        updated_at: new Date().toISOString()
      };
      
      const { data, error } = await supabase
        .from('price_lists')
        .update(priceListData)
        .eq('id', selectedPriceList.id)
        .select();
      
      if (error) throw error;
      
      // Update state with the edited price list
      if (data && data.length > 0) {
        const updatedList = data[0];
        
        // Add customer info if applicable
        if (updatedList.customer_id) {
          const customer = customers.find(c => c.id === updatedList.customer_id);
          updatedList.customer = customer;
        }
        
        setPriceLists(priceLists.map(list => 
          list.id === updatedList.id ? updatedList : list
        ));
        
        setSuccess('Price list updated successfully');
        setShowEditForm(false);
      }
    } catch (err) {
      setError(err.message || 'Failed to update price list');
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      // Parse as local date to avoid timezone shifts
      const [year, month, day] = dateString.split('-');
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  // Filter customers based on search term
  const filteredCustomers = customers.filter(customer => 
    customer.name.toLowerCase().includes(customerSearchTerm.toLowerCase())
  );

  // Customer selection dialog functions
  const handleOpenCustomerSelection = () => {
    setCustomerSearchTerm('');
    setCustomerSelectionDialogOpen(true);
  };

  const handleCloseCustomerSelection = () => {
    setCustomerSelectionDialogOpen(false);
    setCustomerSearchTerm('');
  };

  const handleSelectCustomer = (customer) => {
    setNewPriceList(prev => ({ ...prev, customer_id: customer.id }));
    handleCloseCustomerSelection();
  };

  const getFilteredCustomersForDialog = () => {
    if (!customerSearchTerm.trim()) return customers;
    
    const searchTerm = customerSearchTerm.toLowerCase();
    return customers.filter(customer => 
      customer.name?.toLowerCase().includes(searchTerm) ||
      customer.email?.toLowerCase().includes(searchTerm) ||
      customer.phone?.includes(searchTerm) ||
      customer.company?.toLowerCase().includes(searchTerm) ||
      customer.street?.toLowerCase().includes(searchTerm) ||
      customer.city?.toLowerCase().includes(searchTerm) ||
      customer.state?.toLowerCase().includes(searchTerm)
    );
  };

  // Filter price lists based on search term
  const filteredPriceLists = priceLists.filter(list => 
    list.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (list.description && list.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (list.customer?.name && list.customer.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Back to price lists view
  const handleBackToLists = () => {
    setCurrentPage('list');
    setSelectedPriceList(null);
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Render loading state
  if (loading) {
    return (
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
        <Card sx={{ 
          borderRadius: 3,
          mt: 10,
          mx: 'auto',
          maxWidth: 600,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha('#2196F3', 0.15)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 16px 40px ${alpha('#2196F3', 0.2)}`
          }
        }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress
              size={60}
              thickness={4}
              sx={{
                color: '#2196F3',
                mb: 3,
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round',
                }
              }} 
            />
            <LinearProgress 
              sx={{ 
                mb: 3,
                height: 4,
                borderRadius: 2,
                backgroundColor: alpha('#2196F3', 0.1),
                '& .MuiLinearProgress-bar': {
                  borderRadius: 2,
                  backgroundColor: '#2196F3'
                }
              }} 
            />
            <Typography variant="h6" sx={{
              fontWeight: 500,
              color: '#2196F3',
              mb: 1
            }}>
              Loading Price Lists...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              Preparing your pricing data
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Main price lists view
  if (currentPage === 'list') {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Fade in timeout={800}>
          <Box>
          <Container 
            maxWidth="xl"
            sx={{
              py: 3,
              pb: 8,
              px: { xs: 2, sm: 3 },
              width: '100%',
              maxWidth: '100vw !important'
            }}
          >
        {/* Header Section */}
        <Box sx={{ mb: 3 }}>
          {/* Enhanced Page Header */}
          <Card sx={{
            borderRadius: 3,
            mt: 5,
            mb: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: theme.palette.grey[100],
            backdropFilter: 'blur(20px)',
            boxShadow: 'none',
            borderTop: '3px solid #2196F3',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 6px 20px ${alpha('#2196F3', 0.15)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
                <Box sx={{
                  p: 2,
                  borderRadius: 3,
                  background: alpha('#2196F3', 0.08),
                  border: `1px solid ${alpha('#2196F3', 0.15)}`,
                }}>
                  <LocalOfferIcon sx={{ fontSize: 32, color: '#2196F3' }} />
                </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" sx={{
                  fontWeight: 500,
                  color: '#2196F3',
                  mb: 1,
                  fontSize: '2rem',
                  letterSpacing: '-0.5px'
                }}>
                  Price Lists Management
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: 'text.secondary', 
                  fontWeight: 500,
                  fontSize: '1rem'
                }}>
                  {companyName || 'Loading...'}
                </Typography>
              </Box>
              {hasPermission('sales.price_lists.edit', 'write') && (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setShowCreateForm(true)}
                  sx={{ 
                    borderRadius: 2,
                    borderColor: 'grey.400',
                    color: 'primary.main',
                    borderWidth: 1,
                    fontWeight: 500,
                    px: 3,
                    py: 1.5,
                    background: 'white',
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    '&:hover': {
                      borderColor: 'grey.600',
                      backgroundColor: 'white',
                      transform: 'translateY(-1px)',
                      boxShadow: `0 4px 12px ${alpha(theme.palette.grey[400], 0.25)}`
                    }
                  }}
                >
                  Create Price List
                </Button>
              )}
            </Box>
              <Typography variant="body1" sx={{ 
                color: 'text.secondary', 
                maxWidth: 700, 
                lineHeight: 1.6,
                fontSize: '1.05rem',
                fontWeight: 500
              }}>
                Create and manage price lists for your products with flexible pricing strategies for different customer segments and market conditions.
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Metrics Widget Cards */}
        <Box sx={{ 
          mb: 5, 
          px: 1,
          animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '@keyframes slideInUp': {
            '0%': {
              transform: 'translateY(100%)',
              opacity: 0,
            },
            '100%': {
              transform: 'translateY(0)',
              opacity: 1,
            }
          }
        }}>
          <Grid container spacing={6} sx={{ maxWidth: '100%', width: '100%' }}>
            <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
              <Card 
                sx={{ 
                  height: '100%',
                  background: `linear-gradient(135deg, ${alpha(theme.palette.grey[400], 0.08)}, ${alpha(theme.palette.grey[400], 0.02)})`,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  borderRadius: 3,
                  boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'default',
                  '&:hover': { 
                    transform: 'translateY(-4px)',
                    boxShadow: `0 20px 40px ${alpha(theme.palette.grey[400], 0.25)}`,
                    bgcolor: 'white'
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.grey[400], 0.03)}, transparent)`,
                    zIndex: 0,
                    transition: 'opacity 0.3s ease',
                  },
                  '&:hover::before': {
                    opacity: 0.8
                  }
                }}
              >
                <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="h3" sx={{ 
                        fontWeight: 500, 
                        color: 'text.primary', 
                        mb: 1,
                        fontSize: '2rem',
                        lineHeight: 1
                      }}>
                        {priceLists.length}
                      </Typography>
                      <Typography variant="body1" color="text.primary" sx={{ 
                        fontWeight: 500,
                        fontSize: '1rem'
                      }}>
                        Total Price Lists
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mt: 0.5 }}>
                        {priceLists.filter(list => !list.is_active).length} inactive
                      </Typography>
                    </Box>
                    <Box 
                      sx={{ 
                        p: 2.5, 
                        borderRadius: 3, 
                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`
                      }}
                    >
                      <LocalOfferIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
              <Card 
                sx={{ 
                  height: '100%',
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)}, ${alpha(theme.palette.success.main, 0.02)})`,
                  border: `1px solid ${theme.palette.success.main}`,
                  borderRadius: 3,
                  boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'default',
                  '&:hover': { 
                    transform: 'translateY(-4px)',
                    boxShadow: `0 20px 40px ${alpha(theme.palette.success.main, 0.25)}`,
                    bgcolor: 'white'
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.03)}, transparent)`,
                    zIndex: 0,
                    transition: 'opacity 0.3s ease',
                  },
                  '&:hover::before': {
                    opacity: 0.8
                  }
                }}
              >
                <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="h3" sx={{ 
                        fontWeight: 500, 
                        color: 'success.main', 
                        mb: 1,
                        fontSize: '2rem',
                        lineHeight: 1
                      }}>
                        {priceLists.filter(list => list.is_active).length}
                      </Typography>
                      <Typography variant="body1" color="text.primary" sx={{ 
                        fontWeight: 500,
                        fontSize: '1rem'
                      }}>
                        Active Lists
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mt: 0.5 }}>
                        {priceLists.length > 0 ? Math.round((priceLists.filter(list => list.is_active).length / priceLists.length) * 100) : 0}% of total
                      </Typography>
                    </Box>
                    <Box 
                      sx={{ 
                        p: 2.5, 
                        borderRadius: 3, 
                        background: theme.palette.success.main,
                        color: 'white',
                        boxShadow: `0 6px 18px ${alpha(theme.palette.success.main, 0.3)}`,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                      }}
                    >
                      <AssignmentIcon sx={{ fontSize: 32 }} />
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
              <Card 
                sx={{ 
                  height: '100%',
                  background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.08)}, ${alpha(theme.palette.warning.main, 0.02)})`,
                  border: `1px solid ${theme.palette.warning.main}`,
                  borderRadius: 3,
                  boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'default',
                  '&:hover': { 
                    transform: 'translateY(-4px)',
                    boxShadow: `0 20px 40px ${alpha(theme.palette.warning.main, 0.25)}`,
                    bgcolor: 'white'
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.03)}, transparent)`,
                    zIndex: 0,
                    transition: 'opacity 0.3s ease',
                  },
                  '&:hover::before': {
                    opacity: 0.8
                  }
                }}
              >
                <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="h3" sx={{ 
                        fontWeight: 500, 
                        color: 'warning.main', 
                        mb: 1,
                        fontSize: '2rem',
                        lineHeight: 1
                      }}>
                        {getExpiringPriceLists().length}
                      </Typography>
                      <Typography variant="body1" color="text.primary" sx={{ 
                        fontWeight: 500,
                        fontSize: '1rem'
                      }}>
                        Expiring Soon
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mt: 0.5 }}>
                        Requiring attention
                      </Typography>
                    </Box>
                    <Box 
                      sx={{ 
                        p: 2.5, 
                        borderRadius: 3, 
                        background: theme.palette.warning.main,
                        color: 'white',
                        boxShadow: `0 6px 18px ${alpha(theme.palette.warning.main, 0.3)}`,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                      }}
                    >
                      <WarningAmberIcon sx={{ fontSize: 32 }} />
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
              <Card 
                sx={{ 
                  height: '100%',
                  background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.08)}, ${alpha(theme.palette.info.main, 0.02)})`,
                  border: `1px solid ${theme.palette.info.main}`,
                  borderRadius: 3,
                  boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'default',
                  '&:hover': { 
                    transform: 'translateY(-4px)',
                    boxShadow: `0 20px 40px ${alpha(theme.palette.info.main, 0.25)}`,
                    bgcolor: 'white'
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.03)}, transparent)`,
                    zIndex: 0,
                    transition: 'opacity 0.3s ease',
                  },
                  '&:hover::before': {
                    opacity: 0.8
                  }
                }}
              >
                <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                                                                     <Typography variant="h3" sx={{ 
                          fontWeight: 500, 
                          color: 'info.main', 
                          mb: 1.5,
                          fontSize: '2.5rem',
                          lineHeight: 1
                        }}>
                         {(() => {
                           const typeCount = {};
                           priceLists.forEach(list => {
                             typeCount[list.type] = (typeCount[list.type] || 0) + 1;
                           });
                           const maxType = Object.keys(typeCount).reduce((a, b) => typeCount[a] > typeCount[b] ? a : b, 'retail');
                           return typeCount[maxType] || 0;
                         })()}
                       </Typography>
                       <Typography variant="h6" color="text.primary" sx={{ 
                         fontWeight: 500,
                         fontSize: '1.1rem'
                       }}>
                         {(() => {
                           const typeCount = {};
                           priceLists.forEach(list => {
                             typeCount[list.type] = (typeCount[list.type] || 0) + 1;
                           });
                           const maxType = Object.keys(typeCount).reduce((a, b) => typeCount[a] > typeCount[b] ? a : b, 'retail');
                           return maxType.charAt(0).toUpperCase() + maxType.slice(1) + ' Lists';
                         })()}
                       </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mt: 0.5 }}>
                        Most common type
                      </Typography>
                    </Box>
                    <Box 
                      sx={{ 
                        p: 2.5, 
                        borderRadius: 3, 
                        background: theme.palette.info.main,
                        color: 'white',
                        boxShadow: `0 6px 18px ${alpha(theme.palette.info.main, 0.3)}`,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                      }}
                    >
                      <StorefrontIcon sx={{ fontSize: 32 }} />
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Search and Filter Bar */}
        <Paper 
          sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.grey[500], 0.08)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
            animation: 'slideInUp 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
            '@keyframes slideInUp': {
              '0%': {
                transform: 'translateY(100%)',
                opacity: 0,
              },
              '100%': {
                transform: 'translateY(0)',
                opacity: 1,
              }
            },
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            }
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              placeholder="Search price lists by name, type, or customer..."
              variant="outlined"
              size="medium"
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                ),
                sx: { 
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  '& fieldset': { border: 'none' },
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }
              }}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }
              }}
            />
          </Stack>
        </Paper>

        {/* Main Content */}
        <Paper 
          sx={{ 
            borderRadius: 3,
            overflow: 'hidden',
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
            animation: 'slideInUp 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
            '@keyframes slideInUp': {
              '0%': {
                transform: 'translateY(100%)',
                opacity: 0,
              },
              '100%': {
                transform: 'translateY(0)',
                opacity: 1,
              }
            },
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            }
          }}
        >
            {filteredPriceLists.length === 0 ? (
              <Box sx={{ p: 6, textAlign: 'center' }}>
                <LocalOfferIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 3 }} />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 500, mb: 2, fontSize: '1.5rem' }}>
                  No Price Lists Found
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto', fontSize: '1rem', fontWeight: 500 }}>
                  {searchTerm ? 'No price lists match your search criteria. Try a different search or clear filters.' : 'Create your first price list to get started with flexible pricing strategies.'}
                </Typography>
                {hasPermission('sales.price_lists.edit', 'write') && (
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setShowCreateForm(true)}
                    sx={{ 
                      borderRadius: 2,
                      borderColor: 'grey.400',
                      color: 'primary.main',
                      borderWidth: 1,
                      fontWeight: 500,
                      px: 3,
                      py: 1.5,
                      background: 'white',
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      '&:hover': {
                        borderColor: 'grey.600',
                        backgroundColor: 'white',
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.grey[400], 0.25)}`
                      }
                    }}
                  >
                    Create Price List
                  </Button>
                )}
              </Box>
            ) : (
            <>
            <TableContainer sx={{ overflow: 'visible' }}>
              <Table stickyHeader>
                  <TableHead>
                  <TableRow>
                    <TableCell 
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 2,
                        px: 3
                      }}
                    >
                      Price List Name
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 2,
                        px: 3
                      }}
                    >
                      Type
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 2,
                        px: 3
                      }}
                    >
                      Customer
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 2,
                        px: 3
                      }}
                    >
                      Effective Date
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 2,
                        px: 3
                      }}
                    >
                      Expiration Date
                    </TableCell>
                    <TableCell 
                      align="center"
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 2,
                        px: 3,
                        width: 160
                      }}
                    >
                      Actions
                    </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(rowsPerPage > 0
                      ? filteredPriceLists.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      : filteredPriceLists
                    ).map((priceList) => {
                      const expirationStatus = getExpirationStatus(priceList);
                      
                      return (
                      <TableRow 
                        key={priceList.id} 
                        hover
                        onClick={() => hasPermission('sales.price_lists.edit', 'write') ? handleEditPriceList(priceList) : null}
                        sx={{ 
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': { 
                            bgcolor: 'grey.50',
                            transform: 'translateX(4px)',
                            transition: 'all 0.2s ease',
                            boxShadow: 'inset 4px 0 0 black'
                          },
                          '& .MuiTableCell-root': {
                            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                            py: 2.5,
                            px: 3
                          },
                          ...(expirationStatus?.type === 'expired' && {
                            bgcolor: alpha(theme.palette.error.main, 0.04),
                            borderLeft: `4px solid ${theme.palette.error.main}`,
                            '&:hover': { 
                              bgcolor: alpha(theme.palette.error.main, 0.08),
                              transform: 'scale(1.005)',
                              transition: 'all 0.2s ease'
                            }
                          }),
                          ...(expirationStatus?.type === 'expiring' && {
                            bgcolor: alpha(theme.palette.warning.main, 0.04),
                            borderLeft: `4px solid ${theme.palette.warning.main}`,
                            '&:hover': { 
                              bgcolor: alpha(theme.palette.warning.main, 0.08),
                              transform: 'scale(1.005)',
                              transition: 'all 0.2s ease'
                            }
                          })
                        }}
                      >
                        {/* Price List Name */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                fontWeight: 500,
                                color: 'text.primary',
                                fontSize: '0.95rem'
                              }}
                            >
                              {priceList.name}
                            </Typography>
                            {expirationStatus && (
                              <Chip
                                size="small"
                                label={expirationStatus.message}
                                color={expirationStatus.type === 'expired' ? 'error' : 'warning'}
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        
                        {/* Type */}
                        <TableCell>
                          <Chip 
                            label={priceListTypes.find(t => t.value === priceList.type)?.label || priceList.type}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ 
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              borderRadius: 2
                            }}
                          />
                        </TableCell>
                        
                        {/* Customer */}
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 500,
                              color: 'text.primary',
                              fontSize: '0.9rem'
                            }}
                          >
                            {priceList.customer?.name || '-'}
                          </Typography>
                        </TableCell>
                        
                        {/* Effective Date */}
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 500,
                              color: 'text.primary',
                              fontSize: '0.9rem'
                            }}
                          >
                            {formatDate(priceList.effective_date)}
                          </Typography>
                        </TableCell>
                        
                        {/* Expiration Date */}
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 500,
                              color: 'text.primary',
                              fontSize: '0.9rem'
                            }}
                          >
                              {priceList.expiration_date ? formatDate(priceList.expiration_date) : '-'}
                            </Typography>
                        </TableCell>
                        
                        {/* Actions */}
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Tooltip title="View price list">
                              <IconButton 
                                size="small" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewPriceList(priceList, e);
                                }}
                                sx={{ 
                                  color: 'info.main',
                                  border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                                  borderRadius: 2,
                                  transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                  '&:hover': {
                                    bgcolor: 'info.main',
                                    color: 'white',
                                    transform: 'scale(1.05)',
                                    boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.3)}`
                                  }
                                }}
                              >
                                <AssignmentIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit price list details">
                              <IconButton 
                                size="small" 
                                disabled={!hasPermission('sales.price_lists.edit', 'write')}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (hasPermission('sales.price_lists.edit', 'write')) {
                                    handleEditDetails(priceList, e);
                                  }
                                }}
                                sx={{ 
                                  color: hasPermission('sales.price_lists.edit', 'write') ? 'text.primary' : 'text.disabled',
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                  borderRadius: 2,
                                  transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                  '&:hover': {
                                    bgcolor: hasPermission('sales.price_lists.edit', 'write') ? 'primary.main' : 'transparent',
                                    color: hasPermission('sales.price_lists.edit', 'write') ? 'white' : 'text.disabled',
                                    transform: hasPermission('sales.price_lists.edit', 'write') ? 'scale(1.05)' : 'none',
                                    boxShadow: hasPermission('sales.price_lists.edit', 'write') ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}` : 'none'
                                  },
                                  '&:disabled': {
                                    color: 'text.disabled',
                                    border: `1px solid ${alpha(theme.palette.grey[400], 0.2)}`
                                  }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete price list">
                              <IconButton 
                                size="small" 
                                disabled={!hasPermission('sales.price_lists.delete', 'delete')}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (hasPermission('sales.price_lists.delete', 'delete')) {
                                    handleDeleteClick(priceList.id, priceList.name);
                                  }
                                }}
                                sx={{ 
                                  color: hasPermission('sales.price_lists.delete', 'delete') ? 'error.main' : 'text.disabled',
                                  border: `1px solid ${alpha(hasPermission('sales.price_lists.delete', 'delete') ? theme.palette.error.main : theme.palette.grey[400], 0.3)}`,
                                  borderRadius: 2,
                                  transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                  '&:hover': {
                                    bgcolor: hasPermission('sales.price_lists.delete', 'delete') ? 'error.main' : 'transparent',
                                    color: hasPermission('sales.price_lists.delete', 'delete') ? 'white' : 'text.disabled',
                                    transform: hasPermission('sales.price_lists.delete', 'delete') ? 'scale(1.05)' : 'none',
                                    boxShadow: hasPermission('sales.price_lists.delete', 'delete') ? `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}` : 'none'
                                  },
                                  '&:disabled': {
                                    color: 'text.disabled',
                                    border: `1px solid ${alpha(theme.palette.grey[400], 0.2)}`
                                  }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Pagination */}
              <Box sx={{ 
                borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                borderRadius: '0 0 12px 12px'
              }}>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={filteredPriceLists.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{ 
                    px: 3,
                    py: 2,
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      color: theme.palette.text.primary
                    },
                    '& .MuiTablePagination-select': {
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      background: 'white',
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      }
                    },
                    '& .MuiTablePagination-actions': {
                      '& .MuiIconButton-root': {
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        borderRadius: 2,
                        margin: '0 2px',
                        background: 'white',
                        color: theme.palette.primary.main,
                        fontWeight: 500,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          background: theme.palette.primary.main,
                          color: 'white',
                          transform: 'translateY(-1px)',
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                        },
                        '&.Mui-disabled': {
                          background: alpha(theme.palette.grey[300], 0.5),
                          color: theme.palette.grey[500],
                          border: `1px solid ${theme.palette.grey[300]}`,
                          transform: 'none',
                          boxShadow: 'none'
                        }
                      }
                    }
                  }}
                />
              </Box>
            </>
            )}
        </Paper>

        {/* Create Price List Dialog */}
        <Dialog
          open={showCreateForm}
          onClose={() => {
            setShowCreateForm(false);
            setCustomerSearchTerm('');
          }}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: theme.shadows[10],
              background: 'white',
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
            }
          }}
        >
          <DialogTitle sx={{ 
            p: 4, 
            pb: 2,
            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            background: 'white'
          }}>
            <Typography variant="h5" fontWeight={500} sx={{ 
              color: 'primary.main',
              fontSize: '1.5rem',
              letterSpacing: '-0.5px'
            }}>
              Create New Price List
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 500 }}>
              Set up a new pricing structure for your products
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ 
            p: 3, 
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`
          }}>
            <Grid container spacing={3} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={8}>
                <TextField
                  label="Price List Name"
                  name="name"
                  value={newPriceList.name}
                  onChange={handleChange}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiInputBase-root': {
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      fontSize: '0.9rem',
                      '& fieldset': { border: 'none' },
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      transform: 'translate(14px, 1px) scale(1)',
                      '&.Mui-focused': {
                        transform: 'translate(14px, -17px) scale(0.75)'
                      },
                      '&.MuiFormLabel-filled': {
                        transform: 'translate(14px, -17px) scale(0.75)'
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel 
                    id="type-label"
                    sx={{
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      transform: 'translate(14px, 1px) scale(1)',
                      '&.Mui-focused, &.MuiFormLabel-filled': {
                        transform: 'translate(14px, -17px) scale(0.75)'
                      }
                    }}
                  >
                    Price List Type
                  </InputLabel>
                  <Select
                    labelId="type-label"
                    name="type"
                    value={newPriceList.type}
                    onChange={handleChange}
                    label="Price List Type"
                    sx={{
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      '& .MuiSelect-select': {
                        fontSize: '0.9rem',
                        fontWeight: 500
                      },
                      '& .MuiOutlinedInput-notchedOutline': { 
                        border: 'none'
                      },
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    }}
                  >
                    {priceListTypes.map(type => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description (optional)"
                  name="description"
                  value={newPriceList.description}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={3}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      background: 'white'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Effective Date"
                  value={newPriceList.effective_date ? dayjs(newPriceList.effective_date) : null}
                  onChange={(newValue) => setNewPriceList(prev => ({
                    ...prev,
                    effective_date: newValue ? newValue.format('YYYY-MM-DD') : ''
                  }))}
                  sx={{ 
                    width: '100%',
                    '& .MuiOutlinedInput-root': { 
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      '& fieldset': { border: 'none' },
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Expiration Date (optional)"
                  value={newPriceList.expiration_date ? dayjs(newPriceList.expiration_date) : null}
                  onChange={(newValue) => setNewPriceList(prev => ({
                    ...prev,
                    expiration_date: newValue ? newValue.format('YYYY-MM-DD') : ''
                  }))}
                  sx={{ 
                    width: '100%',
                    '& .MuiOutlinedInput-root': { 
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      '& fieldset': { border: 'none' },
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleOpenCustomerSelection}
                  sx={{
                    height: 56,
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    justifyContent: 'flex-start',
                    px: 3,
                    py: 2.5,
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <PersonIcon sx={{ mr: 2, color: 'text.secondary' }} />
                    <Box sx={{ textAlign: 'left', flex: 1 }}>
                      {newPriceList.customer_id ? (
                        <>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                            {customers.find(c => c.id === newPriceList.customer_id)?.name || 'Unknown Customer'}
                          </Typography>
                          {(() => {
                            const customer = customers.find(c => c.id === newPriceList.customer_id);
                            if (customer && (customer.street || customer.city || customer.state)) {
                              return (
                                <Typography variant="caption" color="text.secondary">
                                  {[customer.street, customer.city, customer.state].filter(Boolean).join(', ')}
                                </Typography>
                              );
                            }
                            return null;
                          })()}
                        </>
                      ) : (
                        <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.secondary' }}>
                          Select Customer (optional)
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Button>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    window.open(`/company/${companyId}/customers-list`, '_blank');
                  }}
                  fullWidth
                  sx={{ 
                    height: '56px',
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    fontWeight: 500,
                    background: 'white',
                    '&:hover': {
                      background: alpha(theme.palette.primary.main, 0.08),
                      border: `1px solid ${theme.palette.primary.main}`,
                      transform: 'translateY(-1px)',
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.2)}`
                    }
                  }}
                >
                  New Customer
                </Button>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ 
            p: 4, 
            borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            background: 'white'
          }}>
            <Button 
              onClick={() => {
                setShowCreateForm(false);
                setCustomerSearchTerm('');
              }}
              sx={{ 
                borderRadius: 3,
                px: 3,
                py: 1.5,
                fontSize: '0.9rem',
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                color: 'text.primary',
                '&:hover': {
                  bgcolor: alpha(theme.palette.grey[100], 0.8),
                  border: `1px solid ${theme.palette.primary.main}`,
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleCreatePriceList}
              disabled={!newPriceList.name}
              sx={{ 
                borderRadius: 3,
                px: 4,
                py: 1.5,
                fontSize: '0.9rem',
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                '&:hover': {
                  transform: 'translateY(-1px)',
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.3)}`
                }
              }}
            >
              Create Price List
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Price List Dialog */}
        <Dialog
          open={showEditForm}
          onClose={() => {
            setShowEditForm(false);
            setCustomerSearchTerm('');
          }}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: theme.shadows[10],
              background: 'white',
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
            }
          }}
        >
          <DialogTitle sx={{ 
            p: 4, 
            pb: 2,
            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            background: 'white'
          }}>
            <Typography variant="h5" fontWeight={500} sx={{ 
              color: 'primary.main',
              fontSize: '1.5rem',
              letterSpacing: '-0.5px'
            }}>
              Edit Price List
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 500 }}>
              Update your pricing structure settings
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ 
            p: 3, 
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`
          }}>
            <Grid container spacing={3} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={8}>
                <TextField
                  label="Price List Name"
                  name="name"
                  value={newPriceList.name}
                  onChange={handleChange}
                  fullWidth
                  required
                  InputProps={{
                    sx: { 
                      borderRadius: '20px !important',
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      minHeight: 56,
                      '& input': {
                        py: 2.5,
                        px: 3,
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        minHeight: 'auto'
                      },
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        border: 'none'
                      },
                      '&:hover fieldset': {
                        border: 'none'
                      },
                      '&.Mui-focused fieldset': {
                        border: 'none'
                      }
                    },
                    '& .MuiInputLabel-root': {
                      fontWeight: 500,
                      transform: 'translate(14px, 1px) scale(1)',
                      '&.Mui-focused, &.MuiFormLabel-filled': {
                        transform: 'translate(14px, -17px) scale(0.75)'
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel 
                    id="edit-type-label"
                    sx={{
                      fontWeight: 500,
                      fontSize: '1rem',
                      transform: 'translate(14px, 1px) scale(1)',
                      '&.Mui-focused, &.MuiFormLabel-filled': {
                        transform: 'translate(14px, -17px) scale(0.75)'
                      }
                    }}
                  >
                    Price List Type
                  </InputLabel>
                  <Select
                    labelId="edit-type-label"
                    name="type"
                    value={newPriceList.type}
                    onChange={handleChange}
                    label="Price List Type"
                    sx={{
                      borderRadius: '20px !important',
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      '& .MuiSelect-select': {
                        fontSize: '0.9rem',
                        fontWeight: 500
                      },
                      '& .MuiOutlinedInput-notchedOutline': { 
                        border: 'none',
                        borderRadius: '20px !important'
                      },
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderRadius: '20px !important'
                      },
                      '&.Mui-focused': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderRadius: '20px !important'
                      }
                    }}
                  >
                    {priceListTypes.map(type => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description (optional)"
                  name="description"
                  value={newPriceList.description}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={3}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'white'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Effective Date"
                  value={newPriceList.effective_date ? dayjs(newPriceList.effective_date) : null}
                  onChange={(newValue) => setNewPriceList(prev => ({
                    ...prev,
                    effective_date: newValue ? newValue.format('YYYY-MM-DD') : ''
                  }))}
                  sx={{ 
                    width: '100%',
                    backgroundColor: 'white',
                    '& .MuiOutlinedInput-root': { 
                      borderRadius: 2,
                      '& fieldset': {
                        borderColor: '#000000',
                        borderWidth: '1px'
                      },
                      '&:hover fieldset': {
                        borderColor: '#000000',
                        borderWidth: '2px'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#000000',
                        borderWidth: '2px'
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Expiration Date (optional)"
                  value={newPriceList.expiration_date ? dayjs(newPriceList.expiration_date) : null}
                  onChange={(newValue) => setNewPriceList(prev => ({
                    ...prev,
                    expiration_date: newValue ? newValue.format('YYYY-MM-DD') : ''
                  }))}
                  sx={{ 
                    width: '100%',
                    backgroundColor: 'white',
                    '& .MuiOutlinedInput-root': { 
                      borderRadius: 2,
                      '& fieldset': {
                        borderColor: '#000000',
                        borderWidth: '1px'
                      },
                      '&:hover fieldset': {
                        borderColor: '#000000',
                        borderWidth: '2px'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#000000',
                        borderWidth: '2px'
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleOpenCustomerSelection}
                  sx={{
                    height: 56,
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    justifyContent: 'flex-start',
                    px: 3,
                    py: 2.5,
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <PersonIcon sx={{ mr: 2, color: 'text.secondary' }} />
                    <Box sx={{ textAlign: 'left', flex: 1 }}>
                      {newPriceList.customer_id ? (
                        <>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                            {customers.find(c => c.id === newPriceList.customer_id)?.name || 'Unknown Customer'}
                          </Typography>
                          {(() => {
                            const customer = customers.find(c => c.id === newPriceList.customer_id);
                            if (customer && (customer.street || customer.city || customer.state)) {
                              return (
                                <Typography variant="caption" color="text.secondary">
                                  {[customer.street, customer.city, customer.state].filter(Boolean).join(', ')}
                                </Typography>
                              );
                            }
                            return null;
                          })()}
                        </>
                      ) : (
                        <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.secondary' }}>
                          Select Customer (optional)
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Button>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                                     onClick={() => {
                     window.open(`/company/${companyId}/customers-list`, '_blank');
                   }}
                  fullWidth
                  sx={{ 
                    height: '56px',
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    fontWeight: 500,
                    background: 'white',
                    '&:hover': {
                      background: alpha(theme.palette.primary.main, 0.08),
                      border: `1px solid ${theme.palette.primary.main}`,
                      transform: 'translateY(-1px)',
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.2)}`
                    }
                  }}
                >
                  New Customer
                </Button>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ 
            p: 4, 
            borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            background: 'white'
          }}>
            <Button 
              onClick={() => {
                setShowEditForm(false);
                setCustomerSearchTerm('');
              }}
              sx={{ 
                borderRadius: 3,
                px: 3,
                py: 1.5,
                fontSize: '0.9rem',
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                color: 'text.primary',
                '&:hover': {
                  bgcolor: alpha(theme.palette.grey[100], 0.8),
                  border: `1px solid ${theme.palette.primary.main}`,
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleSavePriceList}
              disabled={!newPriceList.name}
              sx={{ 
                borderRadius: 3,
                px: 4,
                py: 1.5,
                fontSize: '0.9rem',
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                '&:hover': {
                  transform: 'translateY(-1px)',
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.3)}`
                }
              }}
            >
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={handleCancelDelete}
          BackdropProps={{
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.5)'
            }
          }}
          PaperProps={{
            sx: {
              borderRadius: 4,
              overflow: 'hidden',
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: `0 20px 40px ${alpha(theme.palette.error.main, 0.15)}`
            }
          }}
        >
          <DialogTitle sx={{ 
            background: theme.palette.error.main,
            color: 'white',
            p: 3,
            fontWeight: 500
          }}>
            <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '1.2rem' }}>
              Delete Price List
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ p: 3, pt: 6 }}>
            <Typography sx={{ fontSize: '1rem', mt: 2, fontWeight: 500 }}>
              Are you sure you want to delete <strong>"{priceListToDelete?.name}"</strong>? This action cannot be undone and will remove all associated pricing data.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button 
              onClick={handleCancelDelete} 
              sx={{ 
                borderRadius: 3,
                fontSize: '0.9rem',
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                color: 'text.primary',
                '&:hover': {
                  bgcolor: alpha(theme.palette.grey[100], 0.8),
                  border: `1px solid ${theme.palette.primary.main}`
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
              sx={{ 
                borderRadius: 3,
                fontSize: '0.9rem',
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.2)}`,
                '&:hover': {
                  transform: 'translateY(-1px)',
                  border: `1px solid ${theme.palette.error.main}`,
                  boxShadow: `0 6px 16px ${alpha(theme.palette.error.main, 0.3)}`
                }
              }}
            >
              Delete Price List
            </Button>
          </DialogActions>
        </Dialog>

        {/* Expiration Warning Dialog */}
        <Dialog
          open={showExpirationWarning}
          onClose={() => setShowExpirationWarning(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: theme.shadows[10]
            }
          }}
        >
          <DialogTitle sx={{ 
            p: 3, 
            pb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: alpha(theme.palette.warning.main, 0.1),
            borderBottom: 1,
            borderColor: 'divider'
          }}>
            <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Price List Expiration Warning
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              The following price lists require attention due to upcoming or past expiration dates:
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {getExpiringPriceLists().map(priceList => {
                const status = getExpirationStatus(priceList);
                return (
                  <Alert 
                    key={priceList.id}
                    severity={status.type === 'expired' ? 'error' : 'warning'}
                    sx={{ borderRadius: 2 }}
                    action={
                      <Button 
                        size="small" 
                        onClick={() => {
                          setShowExpirationWarning(false);
                          handleEditPriceList(priceList);
                        }}
                        sx={{ borderRadius: 1 }}
                      >
                        Edit
                      </Button>
                    }
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {priceList.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {status.message} • Expiration Date: {formatDate(priceList.expiration_date)}
                      </Typography>
                      {priceList.customer && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Customer: {priceList.customer.name}
                        </Typography>
                      )}
                    </Box>
                  </Alert>
                );
              })}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2 }}>
            <Button 
              onClick={() => setShowExpirationWarning(false)}
              variant="contained"
              sx={{ borderRadius: 2 }}
            >
              Understood
            </Button>
          </DialogActions>
        </Dialog>

        {/* Customer Selection Dialog */}
        <Dialog
          open={customerSelectionDialogOpen}
          onClose={handleCloseCustomerSelection}
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
              maxHeight: '70vh',
              width: '600px'
            }
          }}
        >
          <DialogTitle sx={{ 
                      fontWeight: 500,
          fontSize: '1.2rem',
          color: 'primary.main',
          borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: alpha(theme.palette.primary.main, 0.05),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ mr: 2 }} />
              Select Customer
            </Box>
            <IconButton 
              onClick={handleCloseCustomerSelection} 
              size="small"
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                '&:hover': {
                  transform: 'scale(1.1)',
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
              <TextField
                fullWidth
                placeholder="Search customers by name, email, phone, company, or address..."
                value={customerSearchTerm}
                onChange={(e) => setCustomerSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="action" />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    fontSize: '0.9rem',
                    '& fieldset': { border: 'none' },
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                    },
                    '&.Mui-focused': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                    }
                  }
                }}
              />
            </Box>
                         <TableContainer sx={{ maxHeight: 300 }}>
               <Table>
                 <TableHead>
                   <TableRow>
                     <TableCell sx={{ 
                       fontWeight: 500, 
                       fontSize: '0.9rem',
                       background: alpha(theme.palette.primary.main, 0.05),
                       borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                       pl: 3
                     }}>
                       Customer
                     </TableCell>
                     <TableCell sx={{ 
                       fontWeight: 500, 
                       fontSize: '0.9rem',
                       background: alpha(theme.palette.primary.main, 0.05),
                       borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                       width: 120,
                       textAlign: 'center',
                       pr: 3
                     }}>
                       Action
                     </TableCell>
                   </TableRow>
                 </TableHead>
                <TableBody>
                  {/* None option */}
                  <TableRow
                    hover
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: !newPriceList.customer_id 
                        ? alpha(theme.palette.primary.main, 0.08)
                        : 'transparent',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.05)
                      }
                    }}
                    onClick={() => handleSelectCustomer({ id: null, name: 'General Price List' })}
                  >
                                         <TableCell sx={{ pl: 3 }}>
                       <Box>
                         <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                           General Price List
                         </Typography>
                         <Typography variant="caption" color="text.secondary">
                           No specific customer - applies to all
                         </Typography>
                       </Box>
                     </TableCell>
                     <TableCell sx={{ textAlign: 'center', pr: 3 }}>
                      <Button
                        size="small"
                        variant={!newPriceList.customer_id ? "contained" : "outlined"}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectCustomer({ id: null, name: 'General Price List' });
                        }}
                        sx={{
                          borderRadius: 2,
                          fontWeight: 500,
                          fontSize: '0.8rem',
                          px: 2,
                          py: 0.5,
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                          }
                        }}
                      >
                        {!newPriceList.customer_id ? "Selected" : "Select"}
                      </Button>
                    </TableCell>
                  </TableRow>

                                     {/* Customer options */}
                   {getFilteredCustomersForDialog().length === 0 && customerSearchTerm ? (
                     <TableRow>
                       <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4, px: 3 }}>
                         <Typography color="text.secondary">
                           No customers found matching your search.
                         </Typography>
                       </TableCell>
                     </TableRow>
                   ) : (
                     getFilteredCustomersForDialog().map((customer) => (
                       <TableRow
                         key={customer.id}
                         hover
                         sx={{
                           cursor: 'pointer',
                           backgroundColor: newPriceList.customer_id === customer.id 
                             ? alpha(theme.palette.primary.main, 0.08)
                             : 'transparent',
                           '&:hover': {
                             backgroundColor: alpha(theme.palette.primary.main, 0.05)
                           }
                         }}
                         onClick={() => handleSelectCustomer(customer)}
                       >
                         <TableCell sx={{ pl: 3 }}>
                           <Box>
                             <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                               {customer.name}
                             </Typography>
                             {customer.company && (
                               <Typography variant="caption" color="text.secondary">
                                 {customer.company}
                               </Typography>
                             )}
                           </Box>
                         </TableCell>
                         <TableCell sx={{ textAlign: 'center', pr: 3 }}>
                           <Button
                             size="small"
                             variant={newPriceList.customer_id === customer.id ? "contained" : "outlined"}
                             onClick={(e) => {
                               e.stopPropagation();
                               handleSelectCustomer(customer);
                             }}
                             sx={{
                               borderRadius: 2,
                               fontWeight: 500,
                               fontSize: '0.8rem',
                               px: 2,
                               py: 0.5,
                               '&:hover': {
                                 transform: 'translateY(-1px)',
                                 boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                               }
                             }}
                           >
                             {newPriceList.customer_id === customer.id ? "Selected" : "Select"}
                           </Button>
                         </TableCell>
                       </TableRow>
                     ))
                   )}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
            <Button 
              onClick={handleCloseCustomerSelection}
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                fontWeight: 500,
                px: 3,
                py: 1,
                '&:hover': {
                  transform: 'translateY(-1px)',
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.15)}`
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                window.open(`/company/${companyId}/customers-list`, '_blank');
              }}
              variant="outlined"
              startIcon={<AddIcon />}
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                fontWeight: 500,
                px: 3,
                py: 1,
                '&:hover': {
                  transform: 'translateY(-1px)',
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                }
              }}
            >
              Add New Customer
            </Button>
          </DialogActions>
        </Dialog>

        {/* Custom Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          sx={{
            '& .MuiSnackbarContent-root': {
              minWidth: '350px',
            }
          }}
          TransitionProps={{
            style: {
              transform: snackbar.open ? 'translateX(0)' : 'translateX(100%)',
              transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            }
          }}
        >
          <Box
            sx={{
              background: snackbar.severity === 'error' 
                ? 'linear-gradient(135deg, #1a1a1a 0%, #2d1b1b 50%, #1a1a1a 100%)'
                : 'linear-gradient(135deg, #1a1a1a 0%, #1b2d1b 50%, #1a1a1a 100%)',
              color: 'white',
              borderRadius: 4,
              p: 3,
              minWidth: '350px',
              maxWidth: '400px',
              boxShadow: snackbar.severity === 'error'
                ? '0 16px 40px rgba(255, 68, 68, 0.2), 0 8px 24px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                : '0 16px 40px rgba(76, 175, 80, 0.2), 0 8px 24px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
              border: `1px solid ${snackbar.severity === 'error' ? 'rgba(255, 68, 68, 0.3)' : 'rgba(76, 175, 80, 0.3)'}`,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 3,
              position: 'relative',
              overflow: 'hidden',
              backdropFilter: 'blur(20px)',
              animation: 'slideInBounce 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
              '@keyframes slideInBounce': {
                '0%': {
                  transform: 'translateX(100%) scale(0.8)',
                  opacity: 0,
                },
                '60%': {
                  transform: 'translateX(-5%) scale(1.02)',
                  opacity: 0.9,
                },
                '100%': {
                  transform: 'translateX(0) scale(1)',
                  opacity: 1,
                }
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: snackbar.severity === 'error' 
                  ? 'linear-gradient(90deg, #ff4444, #ff6b6b, #ff8a80)' 
                  : 'linear-gradient(90deg, #4caf50, #66bb6a, #81c784)',
                borderRadius: '4px 4px 0 0',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: snackbar.severity === 'error'
                  ? 'radial-gradient(circle at 20% 20%, rgba(255, 68, 68, 0.1) 0%, transparent 50%)'
                  : 'radial-gradient(circle at 20% 20%, rgba(76, 175, 80, 0.1) 0%, transparent 50%)',
                pointerEvents: 'none',
              }
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: snackbar.severity === 'error' 
                  ? 'linear-gradient(135deg, #ff4444, #ff6b6b)'
                  : 'linear-gradient(135deg, #4caf50, #66bb6a)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                fontWeight: 'bold',
                boxShadow: snackbar.severity === 'error'
                  ? '0 4px 12px rgba(255, 68, 68, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                  : '0 4px 12px rgba(76, 175, 80, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%, 100%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.1)' }
                }
              }}
            >
              {snackbar.severity === 'error' ? '✕' : '✓'}
            </Box>
            <Box sx={{ flex: 1, pt: 0.5 }}>
              <Typography
                sx={{
                  fontSize: '15px',
                  fontWeight: 500,
                  lineHeight: 1.4,
                  mb: 0.5,
                  background: 'linear-gradient(135deg, #ffffff, #e0e0e0)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                {snackbar.severity === 'error' ? 'Error' : 'Success'}
              </Typography>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 400,
                  lineHeight: 1.5,
                  color: 'rgba(255, 255, 255, 0.9)'
                }}
              >
                {snackbar.message}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleCloseSnackbar}
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                width: 28,
                height: 28,
                transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                '&:hover': {
                  color: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  transform: 'scale(1.1) rotate(90deg)',
                  boxShadow: '0 4px 12px rgba(255, 255, 255, 0.2)'
                }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Snackbar>
          </Container>
        </Box>
      </Fade>
      </LocalizationProvider>
    );
  }

  // Price list editor view
  if (currentPage === 'edit' && selectedPriceList) {
    return (
      <PriceListEditor 
        priceList={selectedPriceList}
        products={products}
        companyId={companyId}
        onBack={handleBackToLists}
        formatCurrency={formatCurrency}
        getVariantPrice={getVariantPrice}
        getProductPrice={getProductPrice}
        hasPermission={hasPermission}
      />
    );
  }

  // Fallback
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      <Alert severity="error">Something went wrong. Please refresh the page.</Alert>
    </Container>
  );
}

// PriceListEditor Component
function PriceListEditor({ priceList, products, companyId, onBack, formatCurrency, getVariantPrice, getProductPrice, hasPermission }) {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [priceItems, setPriceItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddProductForm, setShowAddProductForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [priceValues, setPriceValues] = useState({});
  const [casePriceValues, setCasePriceValues] = useState({});
  const [palletPriceValues, setPalletPriceValues] = useState({});
  const [selectedVariantsForDialog, setSelectedVariantsForDialog] = useState({});
  const [showCasePrice, setShowCasePrice] = useState(false);
  const [showPalletPrice, setShowPalletPrice] = useState(false);
  const [brandFilter, setBrandFilter] = useState('all');
  const [manualOrderByBrand, setManualOrderByBrand] = useState({});
  const [draggingItem, setDraggingItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const [termsValue, setTermsValue] = useState('');
  const [termsSaving, setTermsSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  const getOrderedBrandItems = (brandId, items) => {
    const defaultOrder = sortItemsByProductAndSize(items);
    const orderedIds = Array.isArray(manualOrderByBrand[brandId])
      ? manualOrderByBrand[brandId]
      : [];

    if (orderedIds.length === 0) {
      return defaultOrder;
    }

    const itemMap = new Map(defaultOrder.map(item => [item.id, item]));
    const orderedItems = [];

    orderedIds.forEach(id => {
      const item = itemMap.get(id);
      if (item) {
        orderedItems.push(item);
        itemMap.delete(id);
      }
    });

    if (itemMap.size > 0) {
      orderedItems.push(...Array.from(itemMap.values()));
    }

    return orderedItems;
  };

  const handleDragStart = (event, itemId, brandId) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', itemId);
    setDraggingItem({ itemId, brandId });
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDragEnter = (itemId, brandId) => {
    if (!draggingItem || draggingItem.brandId !== brandId) return;
    if (draggingItem.itemId === itemId) return;
    setDragOverItem({ itemId, brandId });
  };

  const handleDrop = (targetItemId, brandId, currentOrder) => {
    if (!draggingItem || draggingItem.brandId !== brandId) return;

    const nextOrder = moveItemInArray(
      currentOrder,
      draggingItem.itemId,
      targetItemId
    );

    if (nextOrder !== currentOrder) {
      setManualOrderByBrand(prev => ({
        ...prev,
        [brandId]: nextOrder
      }));
      setDirty(true);
    }

    setDraggingItem(null);
    setDragOverItem(null);
  };
  
  // Check expiration status for this price list
  const getExpirationStatus = (priceList) => {
    if (!priceList.expiration_date) return null;
    
    const today = new Date();
    const expirationDate = new Date(priceList.expiration_date);
    const daysUntilExpiration = Math.ceil((expirationDate - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiration < 0) {
      return { type: 'expired', days: Math.abs(daysUntilExpiration), message: `Expired ${Math.abs(daysUntilExpiration)} day(s) ago` };
    } else if (daysUntilExpiration <= 7) {
      return { type: 'expiring', days: daysUntilExpiration, message: `Expires in ${daysUntilExpiration} day(s)` };
    } else if (daysUntilExpiration <= 30) {
      return { type: 'warning', days: daysUntilExpiration, message: `Expires in ${daysUntilExpiration} day(s)` };
    }
    
    return null;
  };
  
  const expirationStatus = getExpirationStatus(priceList);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      // Parse as local date to avoid timezone shifts
      const [year, month, day] = dateString.split('-');
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };
  
  // Helper function to format company address
  const formatCompanyAddress = (company) => {
    if (!company) return '';
    
    let address = '';
    
    if (company.address) address += company.address;
    
    if (company.city || company.state || company.zip_code) {
      address += address ? '\n' : '';
      if (company.city) address += company.city;
      if (company.state) {
        if (company.city) address += ', ';
        address += company.state;
      }
      if (company.zip_code) {
        if (company.city || company.state) address += ' ';
        address += company.zip_code;
      }
    }
    
    return address;
  };

  // Inside PriceListEditor component
  const handlePrintInEditor = async () => {
    // Don't print if no items loaded
    if (!priceItems || priceItems.length === 0) {
      setError('No items to print. Please add products to the price list first.');
      return;
    }

    // Get full company information
    let currentCompany = { name: 'Your Company' };
    try {
      const { data: companyData, error } = await supabase
        .from('companies')
        .select('name, address, city, state, zip_code, phone, email, website')
        .eq('id', companyId)
        .single();
        
      if (!error && companyData) {
        currentCompany = companyData;
      }
    } catch (err) {
      // Use default if company fetch fails
    }

    // Build itemsByBrand here to ensure we have the data
    const currentItemsByBrand = {};
    priceItems.forEach(item => {
      if (item.products?.brands?.id) {
        const brandId = item.products.brands.id;
        if (!currentItemsByBrand[brandId]) {
          currentItemsByBrand[brandId] = {
            brand: item.products.brands,
            items: []
          };
        }
        currentItemsByBrand[brandId].items.push(item);
      } else {
        if (!currentItemsByBrand['no-brand']) {
          currentItemsByBrand['no-brand'] = {
            brand: { name: 'No Brand' },
            items: []
          };
        }
        currentItemsByBrand['no-brand'].items.push(item);
      }
    });

    Object.keys(currentItemsByBrand).forEach(brandId => {
      currentItemsByBrand[brandId].items = getOrderedBrandItems(
        brandId,
        currentItemsByBrand[brandId].items
      );
    });
    
    const printCSS = `
      @page { 
        size: auto; 
        margin: 20mm 15mm 20mm 15mm; 
      }
      @media print {
        body { 
          padding: 0 !important; 
          -webkit-print-color-adjust: exact;
          font-size: 10pt;
        }
        .no-print { 
          display: none !important; 
        }
        @page {
          margin: 20mm 15mm 20mm 15mm;
          @top-left { content: ""; }
          @top-center { content: ""; }
          @top-right { content: ""; }
          @bottom-left { content: ""; }
          @bottom-center { content: ""; }
          @bottom-right { content: ""; }
        }
      }
      body {
        font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;
        line-height: 1.3;
        color: #000;
        padding: 16px;
      }
      table {
        width: 100%;
        border-collapse: collapse;
        margin: 10px 0;
        font-size: 11px;
      }
      th, td {
        border: 1px solid #ddd;
        padding: 6px;
        text-align: left;
      }
      th {
        background-color: #f5f5f5;
        font-weight: 600;
        font-size: 11px;
      }
      .brand-header {
        background-color: #f0f0f0;
        font-weight: 600;
        font-size: 12px;
      }
      .price {
        text-align: right;
      }
    `;

    const printTerms = termsValue?.trim() ? termsValue : priceList.terms_and_conditions;
    const printHTML = `
        <div style="display: flex; justify-content: space-between; gap: 60px; margin-bottom: 20px;">
          <div style="flex: 1; max-width: 45%;">
            <h1 style="margin: 0 0 6px 0; font-size: 1.75rem; font-weight: 400; word-wrap: break-word; line-height: 1.2;">${priceList.name}</h1>
            <p style="margin: 2px 0; color: rgba(0, 0, 0, 0.6); font-size: 0.8rem;">
              Type: ${priceList.type.charAt(0).toUpperCase() + priceList.type.slice(1)}
            </p>
            ${priceList.customer ? 
              `<p style="margin: 2px 0; color: rgba(0, 0, 0, 0.6); font-size: 0.8rem;">
                Customer: ${priceList.customer.name}
              </p>` : ''}
            <p style="margin: 2px 0; color: rgba(0, 0, 0, 0.6); font-size: 0.8rem;">
              Effective Date: ${formatDate(priceList.effective_date)}
            </p>
            ${priceList.expiration_date ? 
              `<p style="margin: 2px 0; color: rgba(0, 0, 0, 0.6); font-size: 0.8rem;">
                Expiration Date: ${formatDate(priceList.expiration_date)}
              </p>` : ''}
          </div>
          <div style="text-align: right; flex-shrink: 0;">
            <h2 style="margin: 0 0 6px 0; font-size: 1.1rem; font-weight: 500;">
              ${currentCompany.name}
            </h2>
            ${formatCompanyAddress(currentCompany) ? `<p style="margin: 1px 0; font-size: 0.8rem;">
              ${formatCompanyAddress(currentCompany)}
            </p>` : ''}
            ${currentCompany.phone ? `<p style="margin: 1px 0; font-size: 0.8rem;">
              Phone: ${currentCompany.phone}
            </p>` : ''}
            ${currentCompany.email ? `<p style="margin: 1px 0; font-size: 0.8rem;">
              Email: ${currentCompany.email}
            </p>` : ''}
            ${currentCompany.website ? `<p style="margin: 1px 0; font-size: 0.8rem;">
              Web: ${currentCompany.website}
            </p>` : ''}
          </div>
        </div>

        
        <table>
          <thead>
            <tr>
              <th>Product</th>
              <th>Size</th>
              <th>Barcode / UPC</th>
              <th>Brand</th>
              ${priceList.show_case_price ? '<th class="price">Case Price</th>' : ''}
              ${priceList.show_pallet_price ? '<th class="price">Pallet Price</th>' : ''}
              <th class="price">MSRP</th>
            </tr>
          </thead>
          <tbody>
            ${Object.keys(currentItemsByBrand).map(brandId => {
              const brandGroup = currentItemsByBrand[brandId];
              return `
                <tr class="brand-header">
                  <td colspan="${5 + (priceList.show_case_price ? 1 : 0) + (priceList.show_pallet_price ? 1 : 0)}">${brandGroup.brand.name}</td>
                </tr>
                ${brandGroup.items.map(item => {
                  // Calculate prices
                  let basePrice = 0;
                  if (item.variant_id && item.product_variants) {
                    basePrice = getVariantPrice(item.product_variants);
                  } else if (item.is_jsonb_variant) {
                    try {
                      if (item.notes) {
                        const variantData = JSON.parse(item.notes);
                        basePrice = variantData.price || 0;
                      }
                    } catch (e) {
                      
                    }
                  } else {
                    basePrice = getProductPrice(item.products);
                  }
                  
                  // Get variant display name
                  let variantName = '-';
                  if (item.product_variants?.name) {
                    variantName = item.product_variants.name;
                  } else if (item.is_jsonb_variant && item.variant_name) {
                    variantName = item.variant_name;
                  }
                  
                  return `
                    <tr>
                      <td>${item.products?.name || ''}</td>
                      <td>${variantName}</td>
                      <td>${item.product_variants?.barcode || item.products?.barcode || '-'}</td>
                      <td>${item.products?.brands?.name || 'No Brand'}</td>
                      ${priceList.show_case_price ? `<td class="price">${formatCurrency(item.case_price || 0)}</td>` : ''}
                      ${priceList.show_pallet_price ? `<td class="price">${formatCurrency(item.pallet_price || 0)}</td>` : ''}
                      <td class="price">${formatCurrency(basePrice)}</td>
                    </tr>
                  `;
                }).join('')}
              `;
            }).join('')}
          </tbody>
        </table>
        ${printTerms ? `
          <div class="terms-box" style="margin: 20px 0; padding: 12px; border: 1px solid #000;">
            <h3 style="margin: 0 0 10px 0; font-weight: 600; font-size: 0.9rem;">Terms & Conditions</h3>
            <div style="white-space: pre-line; font-size: 0.75rem; line-height: 1.4;">
              ${printTerms}
            </div>
          </div>
        ` : ''}
      `;
      
      const iframeId = 'price-list-editor-print-frame';
      let iframe = document.getElementById(iframeId);
      
      if (iframe) {
        document.body.removeChild(iframe);
      }
      
      iframe = document.createElement('iframe');
      iframe.id = iframeId;
      iframe.style.position = 'absolute';
      iframe.style.top = '-9999px';
      iframe.style.left = '-9999px';
      iframe.style.width = '0';
      iframe.style.height = '0';
      document.body.appendChild(iframe);
      
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      
      iframeDoc.open();
      iframeDoc.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title></title>
          <style>${printCSS}</style>
        </head>
        <body>
          ${printHTML}
        </body>
        </html>
      `);
      iframeDoc.close();
      
      iframe.onload = () => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
          
          setTimeout(() => {
            if (document.body.contains(iframe)) {
              document.body.removeChild(iframe);
            }
          }, 1000);
        } catch (error) {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
        }
      };
  };
  
  // Load price items for this price list
  useEffect(() => {
    // Update document title
    document.title = `${priceList.name} - Price List Editor`;
    
    // Set initial checkbox states
    setShowCasePrice(priceList.show_case_price || false);
    setShowPalletPrice(priceList.show_pallet_price || false);
    
    const fetchPriceItems = async () => {
      setLoading(true);
      try {
        
        // Check authentication
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError || !session) {
          setError('Authentication error. Please try logging in again.');
          setLoading(false);
          return;
        }
        
        // First get the price list to verify company_id
        const { data: priceListData, error: priceListError } = await supabase
          .from('price_lists')
          .select('company_id')
          .eq('id', priceList.id)
          .single();
          
        if (priceListError) {
          throw priceListError;
        }
        
        if (!priceListData || priceListData.company_id !== companyId) {
          setError('You do not have permission to access this price list');
          setLoading(false);
          return;
        }
        
        // Get price list items with product information
        const { data: items, error } = await supabase
          .from('price_list_items')
          .select(`
            *
          `)
          .eq('price_list_id', priceList.id);
        
        if (error) throw error;
        
        // Get products and variants separately
        const productIds = [...new Set(items.map(item => item.product_id))];
        const variantIds = items.filter(item => item.variant_id).map(item => item.variant_id);
        
        const { data: productsData, error: productsError } = await supabase
          .from('products')
          .select('*, brands(*)')
          .in('id', productIds);
          
        if (productsError) throw productsError;
        
        let variantsData = [];
        if (variantIds.length > 0) {
          const { data: variants, error: variantsError } = await supabase
            .from('product_variants')
            .select('*')
            .in('id', variantIds);
            
          if (variantsError) throw variantsError;
          variantsData = variants || [];
        }
        
        // Merge the data
        const enhancedItems = items.map(item => {
          const product = productsData.find(p => p.id === item.product_id);
          const variant = variantsData.find(v => v.id === item.variant_id);
          
          return {
            ...item,
            products: product,
            product_variants: variant
          };
        });
        
        if (error) throw error;
        
        
        // Enhanced items with special handling for JSONB variants
        const finalItems = enhancedItems.map(item => {
          // Calculate the base price for comparison
          let basePrice = 0;
          
          // Handle different types of variants and pricing
          if (item.variant_id && item.product_variants) {
            // DB variant price
            basePrice = getVariantPrice(item.product_variants);
          }
          else if (item.is_jsonb_variant) {
            // JSONB variant price
            try {
              if (item.notes) {
                const variantData = JSON.parse(item.notes);
                basePrice = variantData.price || 0;
              }
            } catch (e) {
              
            }
          }
          else {
            // Base product price
            basePrice = getProductPrice(item.products);
          }
          
          // Return enhanced item with base price
          return {
            ...item,
            basePrice
          };
        });
        
        // Find any items with zero price that should have display prices
        const itemsToUpdate = finalItems.filter(item => 
          item.price === 0 && item.basePrice > 0
        );
        
        // Update items with zero prices to match their base prices
        if (itemsToUpdate.length > 0) {
          
          for (const item of itemsToUpdate) {
            
            const { error } = await supabase
              .from('price_list_items')
              .update({ price: item.basePrice })
              .eq('id', item.id);
            
            if (!error) {
              // Update local state
              item.price = item.basePrice;
            }
          }
        }
        
        setPriceItems(finalItems);
        
        // Check if we need to auto-print
        if (priceList.autoPrint) {
          // Wait for data to load then trigger print directly using iframe approach
          setTimeout(() => {
            handlePrintInEditor();
            // Go back immediately after triggering print
            setTimeout(() => {
              onBack();
            }, 100);
          }, 1000);
        }
      } catch (err) {
        setError(err.message || 'Failed to load price items');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPriceItems();
  }, [priceList.id]);

  const orderStorageKey = `priceListOrder:${priceList.id}`;

  useEffect(() => {
    try {
      const storedOrder = localStorage.getItem(orderStorageKey);
      if (storedOrder) {
        const parsed = JSON.parse(storedOrder);
        if (parsed && typeof parsed === 'object') {
          setManualOrderByBrand(parsed);
        }
      }
    } catch (err) {
      // Ignore stored order if it fails to parse
    }
  }, [orderStorageKey]);

  useEffect(() => {
    setTermsValue(priceList.terms_and_conditions || '');
  }, [priceList.id, priceList.terms_and_conditions]);

  useEffect(() => {
    if (!priceItems || priceItems.length === 0) return;

    setManualOrderByBrand(prev => {
      const next = { ...prev };
      const itemsByBrand = {};
      priceItems.forEach(item => {
        const brandId = getBrandIdForItem(item);
        if (!itemsByBrand[brandId]) itemsByBrand[brandId] = [];
        itemsByBrand[brandId].push(item);
      });

      Object.keys(itemsByBrand).forEach(brandId => {
        const defaultOrder = sortItemsByProductAndSize(itemsByBrand[brandId]).map(item => item.id);
        const itemsWithOrder = itemsByBrand[brandId]
          .filter(item => item.sort_order !== null && item.sort_order !== undefined)
          .sort((a, b) => a.sort_order - b.sort_order)
          .map(item => item.id);
        const existingOrder = itemsWithOrder.length > 0
          ? itemsWithOrder
          : (Array.isArray(next[brandId]) ? next[brandId] : []);
        const mergedOrder = [
          ...existingOrder.filter(id => defaultOrder.includes(id)),
          ...defaultOrder.filter(id => !existingOrder.includes(id))
        ];
        next[brandId] = mergedOrder;
      });

      return next;
    });
  }, [priceItems]);

  useEffect(() => {
    try {
      localStorage.setItem(orderStorageKey, JSON.stringify(manualOrderByBrand));
    } catch (err) {
      // Ignore storage failures
    }
  }, [manualOrderByBrand, orderStorageKey]);

  const fetchTermsTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('terms_templates')
        .select('*')
        .eq('company_id', companyId)
        .in('template_type', ['price_list', 'both'])
        .order('template_name');

      if (error) throw error;
      setTemplates(data || []);
    } catch (err) {
      // Keep silent for now
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchTermsTemplates();
    }
  }, [companyId]);

  // Handle changes to price items
  const handlePriceChange = (itemId, value) => {
    // Update the dirty state to indicate changes need to be saved
    setDirty(true);
    
    // Update the price value in state
    setPriceValues({
      ...priceValues,
      [itemId]: value
    });
  };

  // Handle changes to case price items
  const handleCasePriceChange = (itemId, value) => {
    setDirty(true);
    setCasePriceValues({
      ...casePriceValues,
      [itemId]: value
    });
  };

  // Handle changes to pallet price items
  const handlePalletPriceChange = (itemId, value) => {
    setDirty(true);
    setPalletPriceValues({
      ...palletPriceValues,
      [itemId]: value
    });
  };

  const getOrderedItemsForSave = () => {
    const itemsByBrand = {};
    priceItems.forEach(item => {
      const brandId = getBrandIdForItem(item);
      if (!itemsByBrand[brandId]) {
        itemsByBrand[brandId] = {
          brand: item.products?.brands || { name: 'No Brand' },
          items: []
        };
      }
      itemsByBrand[brandId].items.push(item);
    });

    return Object.keys(itemsByBrand)
      .sort((a, b) => {
        const nameA = itemsByBrand[a].brand?.name || '';
        const nameB = itemsByBrand[b].brand?.name || '';
        return nameA.localeCompare(nameB);
      })
      .flatMap(brandId => getOrderedBrandItems(brandId, itemsByBrand[brandId].items));
  };

  // Save all price changes
  const handleSaveAll = async () => {
    if (!dirty) return;
    
    // Check permissions before proceeding
    if (!hasPermission('sales.price_lists.edit', 'write')) {
      setError('You do not have permission to save price changes.');
      return;
    }
    
    try {
      // Verify authentication
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        setError('Authentication error. Please try logging in again.');
        return;
      }
      
      const updates = [];
      
      // Create updates for each changed item
      const allChangedItems = new Set([
        ...Object.keys(priceValues),
        ...Object.keys(casePriceValues),
        ...Object.keys(palletPriceValues)
      ]);

      const orderedItemsForSave = getOrderedItemsForSave();
      const orderUpdates = orderedItemsForSave.reduce((acc, item, index) => {
        if (item.sort_order !== index) {
          acc.push({ id: item.id, sort_order: index });
        }
        return acc;
      }, []);

      allChangedItems.forEach(itemId => {
        const updateData = {
          id: itemId,
          updated_at: new Date().toISOString()
        };

        if (priceValues[itemId]) {
          updateData.price = parseFloat(priceValues[itemId]);
        }
        if (casePriceValues[itemId]) {
          updateData.case_price = parseFloat(casePriceValues[itemId]);
        }
        if (palletPriceValues[itemId]) {
          updateData.pallet_price = parseFloat(palletPriceValues[itemId]);
        }

        if (Object.keys(updateData).length > 2) { // More than just id and updated_at
          updates.push(updateData);
        }
      });

      orderUpdates.forEach(update => {
        const existing = updates.find(item => item.id === update.id);
        if (existing) {
          existing.sort_order = update.sort_order;
          return;
        }
        updates.push({
          id: update.id,
          sort_order: update.sort_order,
          updated_at: new Date().toISOString()
        });
      });
      
      if (updates.length === 0) {
        return;
      }
      
      
      // Update each item individually instead of batch update
      let successCount = 0;
      let errorCount = 0;
      
      for (const item of updates) {
        const updateFields = { updated_at: item.updated_at };
        
        if (item.price !== undefined) updateFields.price = item.price;
        if (item.case_price !== undefined) updateFields.case_price = item.case_price;
        if (item.pallet_price !== undefined) updateFields.pallet_price = item.pallet_price;
        if (item.sort_order !== undefined) updateFields.sort_order = item.sort_order;

        const { data, error } = await supabase
          .from('price_list_items')
          .update(updateFields)
          .eq('id', item.id);
        
        if (error) {
          errorCount++;
        } else {
          successCount++;
          
          // Update the item in state
          setPriceItems(items =>
            items.map(stateItem => {
              if (stateItem.id === item.id) {
                const updatedItem = { ...stateItem };
                if (item.price !== undefined) updatedItem.price = item.price;
                if (item.case_price !== undefined) updatedItem.case_price = item.case_price;
                if (item.pallet_price !== undefined) updatedItem.pallet_price = item.pallet_price;
        if (item.sort_order !== undefined) updatedItem.sort_order = item.sort_order;
                return updatedItem;
              }
              return stateItem;
            })
          );
        }
      }
      
      if (errorCount > 0) {
        if (successCount > 0) {
          setSuccess(`Updated ${successCount} items successfully. ${errorCount} items failed.`);
        } else {
          throw new Error(`Failed to update any items. Please check permissions.`);
        }
      } else {
        // Clear dirty state
        setDirty(false);
        
        // Clear the temporary price values
        setPriceValues({});
        setCasePriceValues({});
        setPalletPriceValues({});
        
        setSuccess('Price changes saved successfully');
      }
    } catch (err) {
      setError(err.message || 'Failed to save changes');
    }
  };

  const handleSaveTerms = async () => {
    if (!hasPermission('sales.price_lists.edit', 'write')) {
      setError('You do not have permission to edit price list terms.');
      return;
    }

    setTermsSaving(true);
    try {
      const { error } = await supabase
        .from('price_lists')
        .update({
          terms_and_conditions: termsValue,
          updated_at: new Date().toISOString()
        })
        .eq('id', priceList.id);

      if (error) throw error;

      Object.assign(priceList, { terms_and_conditions: termsValue });
      setSuccess('Terms & Conditions updated successfully');
    } catch (err) {
      setError(err.message || 'Failed to update terms');
    } finally {
      setTermsSaving(false);
    }
  };

  const handleLoadTemplate = (template) => {
    setTermsValue(template.terms_content || '');
    setLoadDialogOpen(false);
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      setError('Please enter a template name');
      return;
    }

    setTermsSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      const { error } = await supabase
        .from('terms_templates')
        .insert([{
          company_id: companyId,
          template_name: templateName.trim(),
          template_type: 'price_list',
          terms_content: termsValue || '',
          created_by: user?.id
        }]);

      if (error) throw error;

      await fetchTermsTemplates();
      setTemplateName('');
      setSaveDialogOpen(false);
      setSuccess('Template saved successfully');
    } catch (err) {
      setError(`Failed to save template: ${err.message}`);
    } finally {
      setTermsSaving(false);
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate({
      ...template,
      editedName: template.template_name,
      editedContent: template.terms_content
    });
    setEditDialogOpen(true);
  };

  const handleUpdateTemplate = async () => {
    if (!editingTemplate?.editedName?.trim()) {
      setError('Please enter a template name');
      return;
    }

    setTermsSaving(true);
    try {
      const { error } = await supabase
        .from('terms_templates')
        .update({
          template_name: editingTemplate.editedName.trim(),
          terms_content: editingTemplate.editedContent,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingTemplate.id);

      if (error) throw error;

      await fetchTermsTemplates();
      setEditingTemplate(null);
      setEditDialogOpen(false);
      setSuccess('Template updated successfully');
    } catch (err) {
      setError(`Failed to update template: ${err.message}`);
    } finally {
      setTermsSaving(false);
    }
  };

  const handleDeleteClick = (template) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;

    setTermsSaving(true);
    try {
      const { error } = await supabase
        .from('terms_templates')
        .delete()
        .eq('id', templateToDelete.id);

      if (error) throw error;

      await fetchTermsTemplates();
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      setSuccess('Template deleted successfully');
    } catch (err) {
      setError(`Failed to delete template: ${err.message}`);
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    } finally {
      setTermsSaving(false);
    }
  };

  // Update the handleAddProducts function to check and set auth
  const handleAddProducts = async () => {
    // Check permissions before proceeding
    if (!hasPermission('sales.price_lists.edit', 'write')) {
      setError('You do not have permission to add products to price lists.');
      return;
    }
    
    try {
      // Check if any products are selected or any variants are selected
      const hasSelectedProducts = selectedProducts.length > 0;
      const hasSelectedVariants = Object.values(selectedVariantsForDialog).some(variants => 
        Object.values(variants).some(selected => selected)
      );
      
      if (!hasSelectedProducts && !hasSelectedVariants) {
        setError('Please select at least one product or variant');
        return;
      }
      
      
      // Check if we're authenticated
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        setError('Authentication error. Please try logging in again.');
        return;
      }
      
      if (!session) {
        setError('Your session may have expired. Please log in again.');
        return;
      }
      
      // Create a map of existing price list items for faster lookup
      const existingItemsMap = {};
      priceItems.forEach(item => {
        // For DB variants
        if (item.product_id && item.variant_id) {
          existingItemsMap[`${item.product_id}-${item.variant_id}`] = item;
        } 
        // For JSONB variants
        else if (item.product_id && item.is_jsonb_variant && item.variant_name) {
          existingItemsMap[`${item.product_id}-jsonb-${item.variant_name}`] = item;
        }
      });
      
      // Array to hold all new items
      let newItems = [];
      
      // Process each selected product
      for (const productId of selectedProducts) {
        const product = products.find(p => p.id === productId);
        if (!product) {
          continue;
        }
        
        // Get the latest product data to ensure we have current prices
        const { data: freshProduct, error: freshProductError } = await supabase
          .from('products')
          .select('*')
          .eq('id', productId)
          .single();

        if (freshProductError) {
          continue; // Skip this product if we can't get fresh data
        }

        // Check if this product has variants selected in the dialog
        const productVariants = selectedVariantsForDialog[productId];
        const hasSelectedVariantsForProduct = productVariants && Object.values(productVariants).some(selected => selected);
        
        // Get variants from both the DB and JSONB
        // 1. First check for DB variants
        const { data: dbVariants, error: dbVariantsError } = await supabase
          .from('product_variants')
          .select('*')
          .eq('product_id', productId);
          
        if (dbVariantsError) {
          
        }
        
        // 2. Then check for JSONB variants
        let jsonbVariants = [];
        if (freshProduct.variants) {
          try {
            if (typeof freshProduct.variants === 'string') {
              jsonbVariants = JSON.parse(freshProduct.variants);
            } else if (Array.isArray(freshProduct.variants)) {
              jsonbVariants = freshProduct.variants;
            } else if (typeof freshProduct.variants === 'object') {
              jsonbVariants = [freshProduct.variants];
            }
            
          } catch (e) {
          }
        }
        
        // Process DB variants first
        if (dbVariants && dbVariants.length > 0) {
          
          for (const variant of dbVariants) {
            // If variants are selected, only add selected ones
            if (hasSelectedVariantsForProduct && !productVariants[variant.id]) {
              continue;
            }
            
            // Skip if variant already exists in the price list
            const lookupKey = `${productId}-${variant.id}`;
            if (existingItemsMap[lookupKey]) {
              continue;
            }
             
            // Add the DB variant
            try {
              // Check if variant ID is valid before inserting
              if (!variant.id) {
                continue;
              }
              
              // Verify the variant exists in the database
              const { data: variantCheck, error: varCheckErr } = await supabase
                .from('product_variants')
                .select('id, name, price')
                .eq('id', variant.id)
                .single();
                
              if (varCheckErr) {
                
              } else if (variantCheck) {
                
              } else {
                
                continue;
              }
              
              const { data, error } = await supabase
                .from('price_list_items')
                .insert([{
                  price_list_id: priceList.id,
                  product_id: productId,
                  variant_id: variant.id,
                  price: variant.price || 0,
                  is_jsonb_variant: false,
                  updated_at: new Date().toISOString()
                }])
                .select();
                
              if (error) {
                if (error.message.includes('row-level security') || error.message.includes('permission')) {
                  setError('Permission error: You may not have access to manage this price list.');
                } else {
                  throw error;
                }
              } else if (data && data.length > 0) {
                newItems.push(data[0]);
              }
            } catch (e) {
              
            }
          }
        }
        
        // Process JSONB variants
        if (jsonbVariants.length > 0) {
          
          for (const variant of jsonbVariants) {
            // Check if this JSONB variant is selected (using its name as ID)
            const variantId = variant.id || variant.name || `variant-${jsonbVariants.indexOf(variant)}`;
            if (hasSelectedVariantsForProduct && !productVariants[variantId]) {
              continue;
            }
            
            // Skip if variant already exists in the price list
            const variantName = variant.name || `Variant-${variant.sku || 'unnamed'}`;
            const lookupKey = `${productId}-jsonb-${variantName}`;
            
            if (existingItemsMap[lookupKey]) {
              continue;
            }
            
            // Add the JSONB variant
            try {
              const { data, error } = await supabase
                .from('price_list_items')
                .insert([{
                  price_list_id: priceList.id,
                  product_id: productId,
                  variant_id: null,
                  is_jsonb_variant: true,
                  variant_name: variantName,
                  variant_sku: variant.sku || null,
                  price: variant.price || 0,
                  notes: JSON.stringify(variant),
                  updated_at: new Date().toISOString()
                }])
                .select();
                
              if (error) {
                
              } else if (data && data.length > 0) {
                newItems.push(data[0]);
              }
            } catch (e) {
              
            }
          }
        }
        

      }
      
      // If we have newly added items, fetch their full details
      if (newItems.length > 0) {
        // Get the new item IDs
        const newItemIds = newItems.map(item => item.id);
        
        // Fetch complete data for the newly added items using separate queries
        const { data: itemsData, error: fetchError } = await supabase
          .from('price_list_items')
          .select('*')
          .in('id', newItemIds);
          
        if (fetchError) {
          
        } else {
          // Get products and variants separately
          const productIds = [...new Set(itemsData.map(item => item.product_id))];
          const variantIds = itemsData.filter(item => item.variant_id).map(item => item.variant_id);
          
          const { data: productsData, error: productsError } = await supabase
            .from('products')
            .select('*, brands(*)')
            .in('id', productIds);
            
          if (productsError) {
            
          }
          
          let variantsData = [];
          if (variantIds.length > 0) {
            const { data: variants, error: variantsError } = await supabase
              .from('product_variants')
              .select('*')
              .in('id', variantIds);
              
            if (variantsError) {
              
            } else {
              variantsData = variants || [];
            }
          }
          
          // Merge the data and process each item to add basePrice
          const processedItems = itemsData.map(item => {
            const product = productsData?.find(p => p.id === item.product_id);
            const variant = variantsData.find(v => v.id === item.variant_id);
            
            let basePrice = 0;
            
            // Handle different types of variants and pricing
            if (item.variant_id && variant) {
              // DB variant price
              basePrice = getVariantPrice(variant);
            }
            else if (item.is_jsonb_variant) {
              // JSONB variant price
              try {
                if (item.notes) {
                  const variantData = JSON.parse(item.notes);
                  basePrice = variantData.price || 0;
                }
              } catch (e) {
                
              }
            }
            else {
              // Base product price
              basePrice = getProductPrice(product);
            }
            
            return {
              ...item,
              products: product,
              product_variants: variant,
              basePrice
            };
          });
          
          // Add to state
          setPriceItems([...priceItems, ...processedItems]);
        }
        
        setSuccess(`${newItems.length} products/variants added to price list`);
      } else {
        setError('No new items could be added. They may already exist in the price list.');
      }
      
      // Reset UI state
      setShowAddProductForm(false);
      setSelectedProducts([]);
      setSelectedVariantsForDialog({});
    } catch (err) {
      setError(err.message || 'Failed to add products');
    }
  };

  // Delete a price item
  const handleDeleteItem = async (itemId) => {
    // Check permissions before proceeding
    if (!hasPermission('sales.price_lists.delete', 'delete')) {
      setError('You do not have permission to delete price items.');
      return;
    }
    
    try {
      // Verify authentication
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        setError('Authentication error. Please try logging in again.');
        return;
      }
      
      // Verify we have the right to delete this item by checking company_id
      const item = priceItems.find(item => item.id === itemId);
      if (!item) {
        setError('Item not found');
        return;
      }
      
      // Get the price list to verify company_id
      const { data: priceListData, error: priceListError } = await supabase
        .from('price_lists')
        .select('company_id')
        .eq('id', priceList.id)
        .single();
        
      if (priceListError) {
        setError('Error verifying price list ownership');
        return;
      }
      
      if (priceListData.company_id !== companyId) {
        setError('You do not have permission to modify this price list');
        return;
      }
      
      const { error } = await supabase
        .from('price_list_items')
        .delete()
        .eq('id', itemId);
      
      if (error) {
        if (error.message.includes('row-level security') || error.message.includes('permission')) {
          setError('Permission error: You may not have access to manage this price list.');
        } else {
          throw error;
        }
        return;
      }
      
      // Remove from state
      setPriceItems(priceItems.filter(item => item.id !== itemId));
      setSuccess('Product removed from price list');
    } catch (err) {
      setError(err.message || 'Failed to remove product');
    }
  };

  // Handle variant selection
  const handleVariantSelection = (productId, variantId, isSelected) => {
    setSelectedVariantsForDialog(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        [variantId]: isSelected
      }
    }));
  };

  // Select all variants for a product
  const handleSelectAllVariants = (productId, variants) => {
    const allSelected = variants.every(variant => 
      selectedVariantsForDialog[productId]?.[variant.id]
    );
    
    const newSelection = {};
    variants.forEach(variant => {
      newSelection[variant.id] = !allSelected;
    });
    
    setSelectedVariantsForDialog(prev => ({
      ...prev,
      [productId]: newSelection
    }));
  };

  // Check if all variants are selected for a product
  const areAllVariantsSelected = (productId, variants) => {
    return variants.every(variant => 
      selectedVariantsForDialog[productId]?.[variant.id]
    );
  };

  // Get selected variants count for a product
  const getSelectedVariantsCount = (productId, variants) => {
    return variants.filter(variant => 
      selectedVariantsForDialog[productId]?.[variant.id]
    ).length;
  };

  // Filter products that are not already in the price list
  // Or that still have variants not in the price list
  const availableProducts = products.filter(product => {
    const searchLower = searchTerm?.toLowerCase() || '';
    
    // Apply search filter first
    if (searchTerm && !product.name.toLowerCase().includes(searchLower) &&
        !(product.barcode && product.barcode.toLowerCase().includes(searchLower)) &&
        !(product.brand?.name && product.brand.name.toLowerCase().includes(searchLower))) {
      return false;
    }
    
    // Handle products with variants
    if (product.has_variants && product.variants && product.variants.length > 0) {
      // Product with variants - check if ALL variants are already in the price list
      const existingVariantIds = priceItems
        .filter(item => item.product_id === product.id && item.variant_id)
        .map(item => item.variant_id);
      
      // If any variant is not in the price list, show the product
      return product.variants.some(variant => 
        !existingVariantIds.includes(variant.id)
      );
    }
    
    // Handle simple products (without variants)
    if (!product.has_variants || !product.variants || product.variants.length === 0) {
      // Check if this simple product is already in the price list
      const isAlreadyInPriceList = priceItems.some(item => 
        item.product_id === product.id && !item.variant_id
      );
      
      // Show if not already in price list
      return !isAlreadyInPriceList;
    }
    
    return false;
  });

  // Filter price items based on search term and brand filter
  const filteredPriceItems = priceItems.filter(item => {
    const matchesSearch =
      item.products?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.products?.barcode && item.products.barcode.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.product_variants?.name && item.product_variants.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.product_variants?.barcode && item.product_variants.barcode.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.products?.brands?.name && item.products.brands.name.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesBrand =
      brandFilter === 'all' || getBrandIdForItem(item) === brandFilter;

    return matchesSearch && matchesBrand;
  });

  const brandOptions = React.useMemo(() => {
    const brandMap = new Map();
    priceItems.forEach(item => {
      const brandId = getBrandIdForItem(item);
      const brandName = item.products?.brands?.name || 'No Brand';
      if (!brandMap.has(brandId)) {
        brandMap.set(brandId, brandName);
      }
    });

    return Array.from(brandMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [priceItems]);

  // Group price items by brand
  const itemsByBrand = {};
  filteredPriceItems.forEach(item => {
    if (item.products?.brands?.id) {
      const brandId = item.products.brands.id;
      if (!itemsByBrand[brandId]) {
        itemsByBrand[brandId] = {
          brand: item.products.brands,
          items: []
        };
      }
      itemsByBrand[brandId].items.push(item);
    } else {
      if (!itemsByBrand['no-brand']) {
        itemsByBrand['no-brand'] = {
          brand: { name: 'No Brand' },
          items: []
        };
      }
      itemsByBrand['no-brand'].items.push(item);
    }
  });

  // Keep same products together and order variants by size (largest first),
  // while allowing manual reordering per brand.
  Object.keys(itemsByBrand).forEach(brandId => {
    itemsByBrand[brandId].items = getOrderedBrandItems(
      brandId,
      itemsByBrand[brandId].items
    );
  });

  const orderedPriceItems = Object.keys(itemsByBrand).flatMap(
    brandId => itemsByBrand[brandId].items
  );

  // Export price list to CSV
  const handleExportCSV = async () => {
    try {
      // Get company name first
      let companyName = '';
      try {
        const { data: company, error } = await supabase
          .from('companies')
          .select('name')
          .eq('id', companyId)
          .single();
          
        if (error) throw error;
        companyName = company?.name || 'Company';
      } catch (err) {
        companyName = 'Company';
      }
      
      // Create CSV content
      let csvContent = "Product,Variant,Barcode,Brand";
      if (priceList.show_case_price) csvContent += ",Case Price";
      if (priceList.show_pallet_price) csvContent += ",Pallet Price";
      csvContent += ",MSRP";
      csvContent += "\n";
      
      // Process all items
      orderedPriceItems.forEach(item => {
        // Get retail price
        let basePrice = 0;
        if (item.variant_id && item.product_variants) {
          basePrice = getVariantPrice(item.product_variants);
        } else if (item.is_jsonb_variant) {
          try {
            if (item.notes) {
              const variantData = JSON.parse(item.notes);
              basePrice = variantData.price || 0;
            }
          } catch (e) {
            
          }
        } else {
          basePrice = getProductPrice(item.products);
        }
        
        // Format the price values properly
        const formattedMsrp = basePrice.toFixed(2);
        const formattedYourPrice = (item.price || 0).toFixed(2);
        
        // Escape any commas in text fields to prevent CSV breakage
        const productName = item.products?.name?.replace(/,/g, " ") || '';
        const variantName = item.product_variants?.name?.replace(/,/g, " ") || '';
        const barcode = (item.product_variants?.barcode || item.products?.barcode || '-').replace(/,/g, " ");
        const brandName = (item.products?.brands?.name || 'No Brand').replace(/,/g, " ");
        
        // Add row to CSV content
        csvContent += `"${productName}","${variantName}","${barcode}","${brandName}"`;
        if (priceList.show_case_price) csvContent += `,"${(item.case_price || 0).toFixed(2)}"`;
        if (priceList.show_pallet_price) csvContent += `,"${(item.pallet_price || 0).toFixed(2)}"`;
        csvContent += `,"${formattedMsrp}"`;
        csvContent += "\n";
      });
      
      // Create downloadable file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      
      // Include company name in the filename
      const safeCompanyName = companyName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const safePriceListName = priceList.name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      link.setAttribute('download', `${safeCompanyName}_${safePriceListName}_pricelist.csv`);
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setSuccess('Price list exported successfully');
    } catch (err) {
      setError('Failed to export price list');
    }
  };

  // Use this function to log detailed variant info for debugging
  const logVariantPriceDetails = (item) => {
    if (!item.products) {
      return;
    }

    if (item.variant_id && item.product_variants) {
      // DB variant
    } 
    else if (item.is_jsonb_variant) {
      // JSONB variant
      let variantData = null;
      try {
        if (item.notes) {
          variantData = JSON.parse(item.notes);
        }
      } catch (e) {
        
      }
      
      if (variantData) {
      }
    } 
    else {
      // Base product
    }
  };

  if (loading) {
    return (
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
        <Card sx={{ 
          borderRadius: 3,
          mt: 10,
          mx: 'auto',
          maxWidth: 600,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha('#2196F3', 0.15)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 16px 40px ${alpha('#2196F3', 0.2)}`
          }
        }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress
              size={60}
              thickness={4}
              sx={{
                color: '#2196F3',
                mb: 3,
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round',
                }
              }} 
            />
            <LinearProgress 
              sx={{ 
                mb: 3,
                height: 4,
                borderRadius: 2,
                backgroundColor: alpha('#2196F3', 0.1),
                '& .MuiLinearProgress-bar': {
                  borderRadius: 2,
                  backgroundColor: '#2196F3'
                }
              }} 
            />
            <Typography variant="h6" sx={{
              fontWeight: 500,
              color: '#2196F3',
              mb: 1
            }}>
              Loading Price Lists...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              Preparing your pricing data
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Fade in timeout={800}>
        <Box>
          <Container 
            maxWidth="xl"
            sx={{
              py: 3,
              pb: 8,
              px: { xs: 2, sm: 3 },
              width: '100%',
              maxWidth: '100vw !important'
            }}
          >
      {/* Header */}
      <Box sx={{ mb: 3, mt: 5}}>
        <Button variant="outlined" onClick={onBack} sx={{ mb: 3, borderRadius: 2 }}>
          Back to Price Lists
        </Button>
        
        {/* Expiration Warning */}
        {expirationStatus && (
          <Alert 
            severity={expirationStatus.type === 'expired' ? 'error' : 'warning'}
            sx={{ mb: 3, borderRadius: 2 }}
            action={
              expirationStatus.type === 'expired' && (
                <Button 
                  size="small" 
                  color="inherit"
                  sx={{ borderRadius: 1 }}
                >
                  Extend Date
                </Button>
              )
            }
          >
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Price List {expirationStatus.type === 'expired' ? 'Expired' : 'Expiring Soon'}
              </Typography>
              <Typography variant="caption">
                {expirationStatus.message} • Expiration Date: {formatDate(priceList.expiration_date)}
              </Typography>
            </Box>
          </Alert>
        )}
        
        {/* Enhanced Page Header */}
        <Card sx={{
          borderRadius: 3,
          mb: 3,
          mt: 2,
          border: `1px solid ${theme.palette.grey[400]}`,
          background: theme.palette.grey[100],
          backdropFilter: 'blur(20px)',
          boxShadow: 'none',
          borderTop: '3px solid #2196F3',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 6px 20px ${alpha('#2196F3', 0.15)}`,
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
              <Box sx={{
                p: 2,
                borderRadius: 3,
                background: alpha('#2196F3', 0.08),
                border: `1px solid ${alpha('#2196F3', 0.15)}`,
              }}>
                <LocalOfferIcon sx={{ fontSize: 32, color: '#2196F3' }} />
              </Box>
            <Box sx={{ flex: 1 }}>
                        <Typography variant="h4" sx={{
            fontWeight: 500,
            color: '#2196F3',
            mb: 1,
            fontSize: '2rem',
            letterSpacing: '-0.5px'
          }}>
                  {priceList.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ 
                    color: 'text.secondary', 
                    fontWeight: 500,
                    fontSize: '1.1rem',
                    mr: 1
                  }}>Type:</Typography>
                    <Chip 
                      label={priceList.type.charAt(0).toUpperCase() + priceList.type.slice(1)}
                    size="medium"
                      color="primary"
                      variant="outlined"
                    sx={{
                      fontWeight: 500,
                      textTransform: 'uppercase',
                      letterSpacing: 0.8,
                      fontSize: '0.75rem',
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                    }}
                    />
                  </Box>
                  {priceList.customer && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ 
                      color: 'text.secondary', 
                      fontWeight: 500,
                      fontSize: '1.1rem',
                      mr: 1
                    }}>Customer:</Typography>
                    <Typography variant="h6" sx={{ 
                      color: 'text.primary', 
                      fontWeight: 500,
                      fontSize: '1.1rem'
                    }}>{priceList.customer.name}</Typography>
                    </Box>
                  )}
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                disabled={!hasPermission('sales.price_lists.edit', 'write')}
                onClick={() => setShowAddProductForm(true)}
                sx={{ 
                  borderRadius: 2,
                  borderColor: 'grey.400',
                  color: 'primary.main',
                  borderWidth: 1,
                  fontWeight: 500,
                  px: 3,
                  py: 1.5,
                  background: 'white',
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  '&:hover': {
                    borderColor: 'grey.600',
                    backgroundColor: 'white',
                    transform: 'translateY(-1px)',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.grey[400], 0.25)}`
                  }
                }}
              >
                Add Products
              </Button>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={handlePrintInEditor}
                size="large"
                sx={{ 
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
                  transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-1px) scale(1.02)',
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.2)}`
                  }
                }}
              >
                Print
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportCSV}
                size="large"
                sx={{ 
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  color: 'black',
                  backgroundColor: 'white',
                  boxShadow: `0 4px 12px ${alpha('#000000', 0.15)}`,
                  transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-1px) scale(1.02)',
                    backgroundColor: 'black',
                    color: 'white',
                    border: `1px solid black`,
                    boxShadow: `0 6px 16px ${alpha('#000000', 0.3)}`
                  }
                }}
              >
                Export
              </Button>
            </Box>
          </Box>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary', 
              maxWidth: 700, 
              lineHeight: 1.6,
              fontSize: '1.05rem',
              fontWeight: 500
            }}>
              Manage pricing for products in this price list with flexible pricing strategies and real-time updates.
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}



      {/* Pricing Options */}
      <Paper 
        sx={{ 
          p: 4, 
          mb: 3, 
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          boxShadow: `0 8px 24px ${alpha(theme.palette.grey[500], 0.08)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ 
              p: 2, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              backdropFilter: 'blur(10px)',
              boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.2)}`
            }}>
              <LocalOfferIcon sx={{ fontSize: 24, color: 'primary.main' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1.2rem', mb: 0.5 }}>
                Pricing Display Options
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Configure which pricing columns to display in your price list
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              p: 2,
              borderRadius: 2,
              background: showCasePrice 
                ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})` 
                : `linear-gradient(135deg, ${alpha(theme.palette.grey[200], 0.6)}, ${alpha(theme.palette.grey[100], 0.8)})`,
              border: `1px solid ${showCasePrice ? alpha(theme.palette.primary.main, 0.3) : alpha(theme.palette.grey[400], 0.3)}`,
              transition: 'all 0.3s ease',
              minWidth: 140
            }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showCasePrice}
                    onChange={async (e) => {
                      const isChecked = e.target.checked;
                      setShowCasePrice(isChecked);
                      
                      const { error } = await supabase
                        .from('price_lists')
                        .update({ show_case_price: isChecked })
                        .eq('id', priceList.id);
                      
                      if (!error) {
                        Object.assign(priceList, { show_case_price: isChecked });
                      } else {
                        setShowCasePrice(!isChecked);
                      }
                    }}
                    sx={{ 
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: 'primary.main',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        },
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: 'primary.main',
                      },
                    }}
                  />
                }
                label=""
                sx={{ m: 0 }}
              />
              <Typography variant="body2" sx={{ 
                fontWeight: 500, 
                color: showCasePrice ? 'primary.main' : 'text.secondary',
                textAlign: 'center',
                mt: 1,
                fontSize: '0.85rem'
              }}>
                Case Pricing
              </Typography>
            </Box>

            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              p: 2,
              borderRadius: 2,
              background: showPalletPrice 
                ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})` 
                : `linear-gradient(135deg, ${alpha(theme.palette.grey[200], 0.6)}, ${alpha(theme.palette.grey[100], 0.8)})`,
              border: `1px solid ${showPalletPrice ? alpha(theme.palette.primary.main, 0.3) : alpha(theme.palette.grey[400], 0.3)}`,
              transition: 'all 0.3s ease',
              minWidth: 140
            }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showPalletPrice}
                    onChange={async (e) => {
                      const isChecked = e.target.checked;
                      setShowPalletPrice(isChecked);
                      
                      const { error } = await supabase
                        .from('price_lists')
                        .update({ show_pallet_price: isChecked })
                        .eq('id', priceList.id);
                      
                      if (!error) {
                        Object.assign(priceList, { show_pallet_price: isChecked });
                      } else {
                        setShowPalletPrice(!isChecked);
                      }
                    }}
                    sx={{ 
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: 'primary.main',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        },
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: 'primary.main',
                      },
                    }}
                  />
                }
                label=""
                sx={{ m: 0 }}
              />
              <Typography variant="body2" sx={{ 
                fontWeight: 500, 
                color: showPalletPrice ? 'primary.main' : 'text.secondary',
                textAlign: 'center',
                mt: 1,
                fontSize: '0.85rem'
              }}>
                Pallet Pricing
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Action Bar */}
      <Paper 
        sx={{ 
          p: 3, 
          mb: 6, 
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
          animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '@keyframes slideInUp': {
            '0%': {
              transform: 'translateY(100%)',
              opacity: 0,
            },
            '100%': {
              transform: 'translateY(0)',
              opacity: 1,
            }
          },
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
          }
        }}
      >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                sx: { 
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  '& fieldset': { border: 'none' },
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }
              }}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: '0.9rem',
                  fontWeight: 500
                }
              }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel id="brand-filter-label">Brand</InputLabel>
                <Select
                  labelId="brand-filter-label"
                  value={brandFilter}
                  label="Brand"
                  onChange={(e) => setBrandFilter(e.target.value)}
                  sx={{
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '& .MuiSelect-select': {
                      fontSize: '0.9rem',
                      fontWeight: 500
                    },
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                    },
                    '&.Mui-focused': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                    }
                  }}
                >
                  <MenuItem value="all">All Brands</MenuItem>
                  {brandOptions.map(brand => (
                    <MenuItem key={brand.id} value={brand.id}>
                      {brand.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="success"
                disabled={!dirty || !hasPermission('sales.price_lists.edit', 'write')}
                onClick={handleSaveAll}
              sx={{ 
                borderRadius: 3,
                px: 4,
                py: 1.5,
                fontWeight: 500,
                fontSize: '0.9rem',
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                boxShadow: `0 6px 18px ${alpha(theme.palette.success.main, 0.25)}`,
                transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-1px) scale(1.02)',
                  boxShadow: `0 8px 24px ${alpha(theme.palette.success.main, 0.35)}`
                }
              }}
              >
                Save All Changes
              </Button>
            </Grid>
          </Grid>
      </Paper>

        {/* Price List Items */}
      <Paper 
        sx={{ 
          borderRadius: 3,
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
          animation: 'slideInUp 0.8s cubic-bezier(0.4, 0.0, 0.2, 1)',
          '@keyframes slideInUp': {
            '0%': {
              transform: 'translateY(100%)',
              opacity: 0,
            },
            '100%': {
              transform: 'translateY(0)',
              opacity: 1,
            }
          },
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
          }
        }}
      >
          {filteredPriceItems.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <AssignmentIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 3 }} />
                            <Typography variant="h5" gutterBottom sx={{ fontWeight: 500, mb: 2, fontSize: '1.5rem' }}>
                  No Products in this Price List
                </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto', fontSize: '1rem', fontWeight: 500 }}>
                {searchTerm ? 'No products match your search criteria.' : 'Add products to get started.'}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                disabled={!hasPermission('sales.price_lists.edit', 'write')}
                onClick={() => setShowAddProductForm(true)}
              sx={{ 
                borderRadius: 3,
                px: 4,
                py: 1.5,
                fontWeight: 500,
                fontSize: '0.9rem',
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
                transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-1px) scale(1.02)',
                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`
                }
              }}
              >
                Add Products
              </Button>
            </Box>
          ) : (
          <TableContainer sx={{ 
            maxHeight: 'calc(100vh - 400px)',
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            '-ms-overflow-style': 'none',
            'scrollbar-width': 'none'
          }}>
            <Table stickyHeader>
                <TableHead>
                <TableRow>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 2,
                      width: 60
                    }}
                  >
                    Order
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}
                  >
                    Product
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}
                  >
                    Size
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}
                  >
                    Barcode
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}
                  >
                    Brand
                  </TableCell>
                  {showCasePrice && (
                    <TableCell 
                      align="center"
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 2,
                        px: 3
                      }}
                    >
                      Case Price
                    </TableCell>
                  )}
                  {showPalletPrice && (
                    <TableCell 
                      align="center"
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        bgcolor: 'white',
                        borderBottom: `2px solid ${theme.palette.primary.main}`,
                        color: 'primary.main',
                        py: 2,
                        px: 3
                      }}
                    >
                      Pallet Price
                    </TableCell>
                  )}
                  <TableCell 
                    align="center"
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}
                  >
                    MSRP
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3,
                      width: 140
                    }}
                  >
                    Actions
                  </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.keys(itemsByBrand).map(brandId => {
                    const brandGroup = itemsByBrand[brandId];
                    const brandItems = brandGroup.items;
                    const brandItemOrder = brandItems.map(item => item.id);
                    return (
                      <React.Fragment key={brandId}>
                      {/* Brand Header Row */}
                      <TableRow>
                        <TableCell 
                          colSpan={7 + (showCasePrice ? 1 : 0) + (showPalletPrice ? 1 : 0)}
                          sx={{ 
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            py: 2,
                            px: 3
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography 
                              variant="h6" 
                              sx={{ 
                                fontWeight: 500,
                                color: 'primary.main',
                                fontSize: '1.1rem'
                              }}
                            >
                            {brandGroup.brand.name}
                            </Typography>
                            <Chip 
                              label={`${brandGroup.items.length} items`}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ ml: 1 }}
                            />
                          </Box>
                          </TableCell>
                        </TableRow>
                      
                      {/* Brand Items */}
                      {brandItems.map(item => (
                          <TableRow 
                            key={item.id}
                          hover
                          draggable
                          onDragStart={(event) => handleDragStart(event, item.id, brandId)}
                          onDragOver={handleDragOver}
                          onDragEnter={() => handleDragEnter(item.id, brandId)}
                          onDrop={() => handleDrop(item.id, brandId, brandItemOrder)}
                          onDragEnd={() => {
                            setDraggingItem(null);
                            setDragOverItem(null);
                          }}
                            sx={{ 
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: 'grey.50',
                              transform: 'translateX(4px)',
                              transition: 'all 0.2s ease',
                              boxShadow: 'inset 4px 0 0 black'
                            },
                            '& .MuiTableCell-root': {
                              borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                              py: 2.5,
                              px: 3
                            },
                            ...(draggingItem?.itemId === item.id && {
                              opacity: 0.6
                            }),
                            ...(dragOverItem?.itemId === item.id &&
                              dragOverItem?.brandId === brandId && {
                              outline: `2px dashed ${theme.palette.primary.main}`,
                              outlineOffset: -2,
                              boxShadow: `inset 0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
                              bgcolor: alpha(theme.palette.primary.main, 0.08)
                            })
                          }}
                        >
                          {/* Drag Handle */}
                          <TableCell sx={{ px: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'grab' }}>
                              <DragIndicatorIcon fontSize="small" />
                            </Box>
                          </TableCell>
                          {/* Product Name */}
                            <TableCell>
                                                        <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: 500,
                              color: 'text.primary',
                              fontSize: '0.95rem'
                            }}
                          >
                            {item.products?.name}
                          </Typography>
                          </TableCell>
                          
                          {/* Variant */}
                          <TableCell>
                            <Chip 
                              label={item.product_variants?.name || item.variant_name || 'Variant'} 
                              size="small" 
                              color="primary"
                              variant="outlined" 
                              sx={{ 
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                borderRadius: 2
                              }}
                            />
                          </TableCell>
                          
                          {/* Barcode */}
                            <TableCell>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontFamily: 'monospace',
                                fontSize: '0.85rem',
                                fontWeight: 500,
                                color: 'text.primary',
                                bgcolor: alpha(theme.palette.grey[100], 0.6),
                                px: 2,
                                py: 0.5,
                                borderRadius: 1,
                                display: 'inline-block'
                              }}
                            >
                              {item.product_variants?.barcode || item.products?.barcode || '-'}
                            </Typography>
                            </TableCell>
                          
                          {/* Brand */}
                            <TableCell>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 500,
                                color: 'text.primary',
                                fontSize: '0.9rem'
                              }}
                            >
                              {item.products?.brands?.name || 'No Brand'}
                            </Typography>
                            </TableCell>
                          
                            {/* Case Price (Conditional) */}
                            {showCasePrice && (
                              <TableCell align="center">
                                <TextField
                                  size="small"
                                  type="number"
                                  value={casePriceValues[item.id] !== undefined ? casePriceValues[item.id] : (item.case_price || '')}
                                  onChange={(e) => handleCasePriceChange(item.id, e.target.value)}
                                  InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                    inputProps: { 
                                      style: { textAlign: 'center' } 
                                  },
                                  sx: { 
                                    borderRadius: 2,
                                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                    background: 'white',
                                    '& fieldset': { border: 'none' },
                                    '&:hover': {
                                      border: `1px solid ${theme.palette.primary.main}`,
                                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                                    },
                                    '&.Mui-focused': {
                                      border: `1px solid ${theme.palette.primary.main}`,
                                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                                    }
                                    }
                                  }}
                                  sx={{ 
                                    width: '120px',
                                  '& .MuiInputBase-input': {
                                    fontSize: '0.9rem',
                                    fontWeight: 500
                                    }
                                  }}
                                />
                              </TableCell>
                            )}
                            
                            {/* Pallet Price (Conditional) */}
                            {showPalletPrice && (
                              <TableCell align="center">
                                <TextField
                                  size="small"
                                  type="number"
                                  value={palletPriceValues[item.id] !== undefined ? palletPriceValues[item.id] : (item.pallet_price || '')}
                                  onChange={(e) => handlePalletPriceChange(item.id, e.target.value)}
                                  InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                    inputProps: { 
                                      style: { textAlign: 'center' } 
                                  },
                                  sx: { 
                                    borderRadius: 2,
                                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                    background: 'white',
                                    '& fieldset': { border: 'none' },
                                    '&:hover': {
                                      border: `1px solid ${theme.palette.primary.main}`,
                                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                                    },
                                    '&.Mui-focused': {
                                      border: `1px solid ${theme.palette.primary.main}`,
                                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                                    }
                                    }
                                  }}
                                  sx={{ 
                                    width: '120px',
                                  '& .MuiInputBase-input': {
                                    fontSize: '0.9rem',
                                    fontWeight: 500
                                    }
                                  }}
                                />
                              </TableCell>
                            )}

                            {/* MSRP */}
                            <TableCell align="center">
                              <Typography 
                                variant="body1" 
                                sx={{ 
                                  fontWeight: 500,
                                  color: 'text.primary',
                                  fontSize: '0.95rem',
                                  bgcolor: alpha(theme.palette.grey[100], 0.6),
                                  px: 2,
                                  py: 0.8,
                                  borderRadius: 2,
                                  display: 'inline-block'
                                }}
                              >
                                  {(() => {
                                    let basePrice = 0;
                                    
                                    if (item.variant_id && item.product_variants) {
                                      basePrice = getVariantPrice(item.product_variants);
                                    }
                                    else if (item.is_jsonb_variant) {
                                      try {
                                        if (item.notes) {
                                          const variantData = JSON.parse(item.notes);
                                          basePrice = variantData.price || 0;
                                        }
                                      } catch (e) {
                                      // Handle error
                                      }
                                    }
                                    
                                    return formatCurrency(basePrice);
                                  })()}
                                </Typography>
                              </TableCell>
                          
                          {/* Actions */}
                            <TableCell align="center">
                              <Tooltip title="Delete Item">
                                <IconButton 
                                  size="small" 
                                  disabled={!hasPermission('sales.price_lists.delete', 'delete')}
                                  onClick={() => handleDeleteItem(item.id)}
                                sx={{
                                  color: hasPermission('sales.price_lists.delete', 'delete') ? theme.palette.error.main : 'text.disabled',
                                  border: `1px solid ${alpha(hasPermission('sales.price_lists.delete', 'delete') ? theme.palette.error.main : theme.palette.grey[400], 0.3)}`,
                                  borderRadius: 2,
                                  transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                  '&:hover': {
                                    bgcolor: hasPermission('sales.price_lists.delete', 'delete') ? 'error.main' : 'transparent',
                                    color: hasPermission('sales.price_lists.delete', 'delete') ? 'white' : 'text.disabled',
                                    transform: hasPermission('sales.price_lists.delete', 'delete') ? 'scale(1.05)' : 'none',
                                    boxShadow: hasPermission('sales.price_lists.delete', 'delete') ? `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}` : 'none'
                                  },
                                  '&:disabled': {
                                    color: 'text.disabled',
                                    border: `1px solid ${alpha(theme.palette.grey[400], 0.2)}`
                                  }
                                }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
      </Paper>

      {/* Terms & Conditions */}
      <Paper
        sx={{
          p: 4,
          mt: 8,
          mb: 6,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          boxShadow: `0 8px 24px ${alpha(theme.palette.grey[500], 0.08)}`
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              p: 2,
              borderRadius: 3,
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            }}>
              <GavelIcon sx={{ fontSize: 24, color: 'primary.main' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1.2rem', mb: 0.5 }}>
                Terms & Conditions
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Add terms to appear at the bottom of the printed price list
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<FolderOpenIcon />}
              onClick={() => setLoadDialogOpen(true)}
              sx={{ borderRadius: 2 }}
            >
              Load Template
            </Button>
            <Button
              variant="outlined"
              startIcon={<SaveAsIcon />}
              onClick={() => setSaveDialogOpen(true)}
              sx={{ borderRadius: 2 }}
            >
              Save as Template
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveTerms}
              disabled={termsSaving}
              sx={{ borderRadius: 2 }}
            >
              {termsSaving ? 'Saving...' : 'Save Terms'}
            </Button>
          </Box>
        </Box>
        <TextField
          label="Terms and Conditions"
          fullWidth
          multiline
          rows={50}
          value={termsValue}
          onChange={(e) => setTermsValue(e.target.value)}
          placeholder="Enter terms and conditions..."
          InputProps={{
            sx: {
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              background: 'white',
              '& fieldset': { border: 'none' },
              fontFamily: 'monospace',
              fontSize: '0.875rem'
            }
          }}
        />
      </Paper>

      {/* Add Products Dialog */}
      <Dialog
        open={showAddProductForm}
        onClose={() => {
          setShowAddProductForm(false);
          setSelectedProducts([]);
          setSelectedVariantsForDialog({});
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: theme.shadows[10],
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 4, 
          pb: 2,
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          background: 'white'
        }}>
          <Typography variant="h5" fontWeight={500} sx={{ 
            color: 'primary.main',
            fontSize: '1.5rem',
            letterSpacing: '-0.5px'
          }}>
            Add Products to Price List
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 500 }}>
            Select products to add to "{priceList.name}"
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ 
          p: 0,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`
        }}>
          <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
            <TextField
              fullWidth
              placeholder="Search products by name, barcode, or brand..."
              onChange={(e) => setSearchTerm(e.target.value)}
              size="medium"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  '& fieldset': { border: 'none' },
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                },
                '& .MuiInputBase-input': {
                  fontSize: '0.9rem',
                  fontWeight: 500
                }
              }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Box>
          
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.grey[100], 0.8) }}>
                  <TableCell sx={{ width: 60, pl: 3, fontWeight: 500 }}></TableCell>
                  <TableCell sx={{ fontWeight: 500, minWidth: 200, pl: 2, pr: 3 }}>Product Name</TableCell>
                  <TableCell sx={{ fontWeight: 500, minWidth: 140, px: 2 }}>Brand</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {availableProducts.map(product => (
                  <React.Fragment key={product.id}>
                    <TableRow 
                      hover
                      selected={selectedProducts.includes(product.id)}
                      onClick={() => {
                        if (selectedProducts.includes(product.id)) {
                          setSelectedProducts(selectedProducts.filter(id => id !== product.id));
                        } else {
                          setSelectedProducts([...selectedProducts, product.id]);
                        }
                      }}
                      sx={{ 
                        cursor: 'pointer',
                        '&.Mui-selected': {
                          bgcolor: alpha(theme.palette.success.main, 0.08),
                          '&:hover': {
                            bgcolor: alpha(theme.palette.success.main, 0.12)
                          }
                        },
                        '&:hover': {
                          bgcolor: alpha(theme.palette.grey[100], 0.8)
                        },
                        borderLeft: selectedProducts.includes(product.id) ? `3px solid ${theme.palette.success.main}` : '3px solid transparent'
                      }}
                    >
                      <TableCell sx={{ width: 60, pl: 3 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          border: 2,
                          borderColor: selectedProducts.includes(product.id) ? 'success.main' : 'grey.400',
                          bgcolor: selectedProducts.includes(product.id) ? 'success.main' : 'transparent',
                          color: selectedProducts.includes(product.id) ? 'white' : 'transparent',
                          transition: 'all 0.2s ease',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          ✓
                        </Box>
                      </TableCell>
                      <TableCell sx={{ pl: 2, pr: 3 }}>
                        <Box>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: 500,
                              mb: 0.5
                            }}
                          >
                            {product.name}
                          </Typography>
                          {product.variants && product.variants.length > 0 ? (
                            <Chip
                              size="small"
                              label={`${product.variants.length} variants`}
                              color="success"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          ) : product.has_variants ? (
                            <Chip
                              size="small"
                              label="Has variants (none found)"
                              color="warning"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          ) : null}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ px: 2 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {product.brand?.name || 'No Brand'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    
                                          {/* Display variant selection when product is selected */}
                      {product.variants && product.variants.length > 0 && selectedProducts.includes(product.id) && (
                        <TableRow sx={{ bgcolor: alpha(theme.palette.success.main, 0.02) }}>
                          <TableCell colSpan={3} sx={{ py: 2, px: 4 }}>
                          <Box sx={{ pl: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ color: 'success.main', fontWeight: 500 }}>
                                Select Variants ({getSelectedVariantsCount(product.id, product.variants)} of {product.variants.length} selected):
                              </Typography>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleSelectAllVariants(product.id, product.variants)}
                                sx={{
                                  minWidth: 'auto',
                                  px: 2,
                                  py: 0.5,
                                  fontSize: '0.75rem',
                                  textTransform: 'none',
                                  color: 'success.main',
                                  borderColor: 'success.main',
                                  '&:hover': {
                                    borderColor: 'success.dark',
                                    bgcolor: alpha(theme.palette.success.main, 0.08)
                                  }
                                }}
                              >
                                {areAllVariantsSelected(product.id, product.variants) ? 'Unselect All' : 'Select All'}
                              </Button>
                            </Box>
                            <Grid container spacing={2}>
                              {product.variants.map((variant, idx) => (
                                <Grid item xs={12} sm={6} md={4} key={variant.id || idx}>
                                  <Paper
                                    sx={{
                                      p: 2,
                                      borderRadius: 2,
                                      border: selectedVariantsForDialog[product.id]?.[variant.id]
                                        ? `2px solid ${theme.palette.success.main}`
                                        : `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                      bgcolor: selectedVariantsForDialog[product.id]?.[variant.id] 
                                        ? alpha(theme.palette.success.main, 0.12)
                                        : alpha(theme.palette.grey[50], 0.8),
                                      cursor: 'pointer',
                                      transition: 'all 0.2s ease-in-out',
                                      '&:hover': {
                                        bgcolor: selectedVariantsForDialog[product.id]?.[variant.id] 
                                          ? alpha(theme.palette.success.main, 0.18)
                                          : alpha(theme.palette.grey[100], 0.8),
                                        transform: 'translateY(-2px)',
                                        boxShadow: theme.shadows[4]
                                      }
                                    }}
                                    onClick={() => handleVariantSelection(product.id, variant.id, !selectedVariantsForDialog[product.id]?.[variant.id])}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                      <Checkbox
                                        checked={!!selectedVariantsForDialog[product.id]?.[variant.id]}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          handleVariantSelection(product.id, variant.id, e.target.checked);
                                        }}
                                        size="small"
                                        color="success"
                                        sx={{ 
                                          p: 0.5,
                                          '&.Mui-checked': {
                                            color: 'success.main'
                                          }
                                        }}
                                      />
                                      <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                                          {variant.name || 
                                           (variant.volume_weight ? `${variant.volume_weight}${variant.volume_unit || ''}` : 'Unnamed variant')}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                          {variant.barcode ? `Barcode: ${variant.barcode}` : (variant.sku ? `SKU: ${variant.sku}` : 'No code')}
                                        </Typography>
                                        <Typography variant="body2" fontWeight={500} color="success.main">
                                          {formatCurrency(parseFloat(variant.price) || parseFloat(product.default_price) || 0)}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Paper>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
                {availableProducts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary">
                          No products available
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                          All products have been added to this price list or no products match your search.
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => {
                            window.open(`/companies/${companyId}/products`, '_blank');
                          }}
                          sx={{
                            borderRadius: 3,
                            px: 4,
                            py: 1.5,
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                            color: 'white',
                            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                            '&:hover': {
                              background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                              transform: 'translateY(-2px)',
                              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.4)}`
                            }
                          }}
                        >
                          Create Your First Product
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ 
          p: 4, 
          borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          background: 'white',
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => {
              window.open(`/company/${companyId}/products`, '_blank');
            }}
            sx={{
              borderRadius: 3,
              px: 3,
              py: 1.5,
              fontSize: '0.9rem',
              fontWeight: 500,
              border: `1px solid ${theme.palette.primary.main}`,
              color: 'primary.main',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                border: `1px solid ${theme.palette.primary.dark}`,
                transform: 'translateY(-1px)'
              }
            }}
          >
            Add New Product
          </Button>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" fontWeight={500}>
              {(() => {
                const productCount = selectedProducts.length;
                const variantCount = Object.values(selectedVariantsForDialog).reduce((total, variants) => 
                  total + Object.values(variants).filter(selected => selected).length, 0
                );
                const totalCount = productCount + variantCount;
                
                if (totalCount === 0) return "No items selected";
                if (variantCount === 0) return `${productCount} product${productCount !== 1 ? 's' : ''} selected`;
                if (productCount === 0) return `${variantCount} variant${variantCount !== 1 ? 's' : ''} selected`;
                return `${productCount} product${productCount !== 1 ? 's' : ''} + ${variantCount} variant${variantCount !== 1 ? 's' : ''} selected`;
              })()}
            </Typography>
            
            <Button 
              onClick={() => {
                setShowAddProductForm(false);
                setSelectedProducts([]);
                setSelectedVariantsForDialog({});
              }}
              sx={{ 
                borderRadius: 3,
                px: 3,
                py: 1.5,
                fontSize: '0.9rem',
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                color: 'text.primary',
                '&:hover': {
                  bgcolor: alpha(theme.palette.grey[100], 0.8),
                  border: `1px solid ${theme.palette.primary.main}`,
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              disabled={!hasPermission('sales.price_lists.edit', 'write') || (() => {
                const productCount = selectedProducts.length;
                const variantCount = Object.values(selectedVariantsForDialog).reduce((total, variants) => 
                  total + Object.values(variants).filter(selected => selected).length, 0
                );
                return productCount === 0 && variantCount === 0;
              })()}
              onClick={handleAddProducts}
              sx={{ 
                borderRadius: 3,
                px: 4,
                py: 1.5,
                fontSize: '0.9rem',
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                color: 'white',
                boxShadow: `0 8px 24px ${alpha(theme.palette.success.main, 0.3)}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`,
                  transform: 'translateY(-2px) scale(1.03)',
                  border: `1px solid ${theme.palette.success.main}`,
                  boxShadow: `0 12px 32px ${alpha(theme.palette.success.main, 0.4)}`
                },
                '&:disabled': {
                  background: `linear-gradient(135deg, ${alpha(theme.palette.grey[400], 0.6)}, ${alpha(theme.palette.grey[500], 0.6)})`,
                  color: alpha(theme.palette.text.primary, 0.4),
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                  boxShadow: 'none'
                }
              }}
            >
              {(() => {
                const productCount = selectedProducts.length;
                const variantCount = Object.values(selectedVariantsForDialog).reduce((total, variants) => 
                  total + Object.values(variants).filter(selected => selected).length, 0
                );
                const totalCount = productCount + variantCount;
                
                if (totalCount === 0) return "Add Items";
                if (variantCount === 0) return `Add ${productCount} Product${productCount !== 1 ? 's' : ''}`;
                if (productCount === 0) return `Add ${variantCount} Variant${variantCount !== 1 ? 's' : ''}`;
                return `Add ${totalCount} Item${totalCount !== 1 ? 's' : ''}`;
              })()}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Load Template Dialog */}
      <Dialog
        open={loadDialogOpen}
        onClose={() => setLoadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        disableScrollLock
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main' }}>
            Load Terms Template
          </Typography>
          <IconButton onClick={() => setLoadDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {templates.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              No saved templates. Create one by clicking "Save as Template".
            </Typography>
          ) : (
            <List>
              {templates.map((template) => (
                <ListItem
                  key={template.id}
                  disablePadding
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton edge="end" onClick={() => handleEditTemplate(template)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton edge="end" onClick={() => handleDeleteClick(template)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemButton onClick={() => handleLoadTemplate(template)}>
                    <ListItemText
                      primary={template.template_name}
                      secondary={`Created ${new Date(template.created_at).toLocaleDateString()}`}
                    />
                    {template.is_default && (
                      <Chip label="Default" size="small" color="primary" sx={{ ml: 1 }} />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoadDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Save Template Dialog */}
      <Dialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        disableScrollLock
      >
        <DialogTitle sx={{ fontWeight: 500, color: 'primary.main' }}>
          Save Terms Template
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Template Name"
            fullWidth
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="e.g., Standard Price List Terms"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveTemplate}
            variant="contained"
            disabled={termsSaving}
          >
            {termsSaving ? 'Saving...' : 'Save Template'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Template Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setEditingTemplate(null);
        }}
        maxWidth="md"
        fullWidth
        disableScrollLock
      >
        <DialogTitle sx={{ fontWeight: 500, color: 'primary.main' }}>
          Edit Terms Template
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Template Name"
            fullWidth
            value={editingTemplate?.editedName || ''}
            onChange={(e) => setEditingTemplate({
              ...editingTemplate,
              editedName: e.target.value
            })}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            label="Template Content"
            multiline
            rows={16}
            fullWidth
            value={editingTemplate?.editedContent || ''}
            onChange={(e) => setEditingTemplate({
              ...editingTemplate,
              editedContent: e.target.value
            })}
            sx={{
              '& .MuiInputBase-root': {
                fontFamily: 'monospace',
                fontSize: '0.9rem'
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditDialogOpen(false);
            setEditingTemplate(null);
          }}>Cancel</Button>
          <Button
            onClick={handleUpdateTemplate}
            variant="contained"
            disabled={termsSaving}
          >
            {termsSaving ? 'Updating...' : 'Update Template'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setTemplateToDelete(null);
        }}
        maxWidth="xs"
        fullWidth
        disableScrollLock
      >
        <DialogTitle sx={{ fontWeight: 500, color: 'error.main' }}>
          Delete Template?
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{templateToDelete?.template_name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteDialogOpen(false);
            setTemplateToDelete(null);
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={termsSaving}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
        </Container>
      </Box>
    </Fade>
    </LocalizationProvider>
  );
} 